import React, { useEffect, useState, useMemo } from 'react';
import { Platform } from 'react-native';
import Input from '@/components/General/inputs/Input';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { savePathStep, deletePathStep, savePathStepEdge, insertStepOnEdge } from '@/data/paths';
import { supabase } from '@/data/client';
import { getMonthlyEventInstances } from '@/data/events';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import SelectInput from '@/components/General/inputs/SelectInput';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import Toggle from '@/components/General/inputs/Toggle.js';
import ToggleInput from '@/components/General/inputs/ToggleInput.js';
import VariableSelectInput from '@/components/Paths/VariableSelectInput';
import VariableTextInsertButton from '@/components/Paths/VariableTextInsertButton';
import V2ActionConfigPanel from '@/components/Paths/v2/V2ActionConfigPanel';
import PeopleTargetInput from '@/components/Paths/v2/PeopleTargetInput';
import AutomationBindingInput from '@/components/Paths/v2/AutomationBindingInput';
import { V2_ACTION_TYPES, V2_ACTION_TYPE_SET, V2_LEGACY_ACTION_SET, ACTION_PERSON_INPUTS, buildActionTypeOptions, buildV2LegacyActionConfig, buildV2VariableOptions, computeGuaranteedUpstreamStepIDs, validateV2ActionDraft, buildResolvePersonConfig, buildUpsertPersonConfig, buildCreateTaskConfig, buildWaitForTaskConfig, buildStartFollowUpPathConfig, buildInvokeAutomationConfig, buildRequestApprovalConfig, buildStepOutputRuleOptions, STEP_OUTPUT_OPERATORS } from '@/utils/automationBuilder';
import { ACTION_PARAM_DEFS, UPDATE_PERSON_FIELDS, PREDEFINED_TASK_TYPES, PREDEFINED_TASK_TYPE_OPTIONS } from '@/components/Paths/flow/constants';
import { AUTOMATION_DUE_TYPES } from '@/utils/constants/taskTypes';
import Toast from 'react-native-toast-message';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import useDropdownStore from '@/data/store/useDropdownStore';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import SheetModal from '@/components/General/SheetModal';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { showAlert } from '@/utils/showAlert';
import { View, Text, TouchableOpacity, Pressable, useWindowDimensions, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import Ionicons from "@react-native-vector-icons/ionicons";
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import Calendar from '@/components/Events/Calendar';

// ─── Condition Rule Config Examples ───────────────────────────────────────────
// All conditions use unified shape: { ruleType, key, operator, value }
//
// ENROLLEE_FIELD (regular):  { ruleType: "ENROLLEE_FIELD", key: "person.age", operator: "less_than", value: "18" }
// ENROLLEE_FIELD (custom):   { ruleType: "ENROLLEE_FIELD", key: "customField:<uuid>", operator: "equals", value: "Some Value" }
// ENROLLEE_FIELD (has/none): { ruleType: "ENROLLEE_FIELD", key: "person.email", operator: "has", value: null }
// ENROLLEE_FIELD (role):     { ruleType: "ENROLLEE_FIELD", key: "person.role", operator: "has", value: "<roleUUID>" }
// TIME_OF_DAY:               { ruleType: "TIME_OF_DAY", key: null, operator: null, value: "morning" }
// SMALL_GROUP_STATUS:        { ruleType: "SMALL_GROUP_STATUS", key: "<smallGroupTypeID>", operator: null, value: "IS_IN_GROUP" }
// TRAINING_STATUS:           { ruleType: "TRAINING_STATUS", key: "<moduleID>", operator: null, value: "HAS_COMPLETED" }
// REACH_USER_STATUS:         { ruleType: "REACH_USER_STATUS", key: null, operator: null, value: "IS_REACH_USER" }
// FORM_FIELD:                { ruleType: "FORM_FIELD", key: "<fieldId>", operator: "equals", value: "Some Value" }
// FORM_FIELD (checkbox):     { ruleType: "FORM_FIELD", key: "<fieldId>", operator: "equals", value: true }
// FORM_FIELD (is_empty):     { ruleType: "FORM_FIELD", key: "<fieldId>", operator: "is_empty", value: null }
//                            FORM_FIELD is only offered when the path trigger is FORM_ENTRY. key is the
//                            form field's fieldId (matched across all forms connected to this path);
//                            value is a string, boolean (checkbox fields), or null.
//
// Condition step config shape:
// { branches: [{ id: "branch_1", label: "Under 18", conditions: [...] }, ...] }
// Each branch leads to its own sub-flow. Conditions within a branch use AND logic.
// ──────────────────────────────────────────────────────────────────────────────

// v1 action types (every legacy PathSteps.actionType). Labels/descriptions
// live in ACTION_TYPE_META (automationBuilder) — the Action Type dropdown is
// built by buildActionTypeOptions so v1 and v2 share section/subtitle metadata.
const V1_ACTION_TYPES = [
    'ADD_TO_ROLE', 'ADD_TO_SMALL_GROUP', 'CHECK_INTO_EVENT', 'ENROLL_TRAINING',
    'INVITE_TO_REACH', 'POST_TO_BOARD', 'REGISTER_FOR_EVENT', 'SEND_EMAIL',
    'SEND_SMS', 'TRIGGER_PATH', 'UPDATE_PERSON',
];

const advanceOnOptions = [
    { value: 'AUTOMATIC', label: 'Automatic' },
    { value: 'MANUAL', label: 'Manual' },
];

const advanceOnHelpText = 'Automatic fires immediately and continues to the next step. Manual waits for someone to open the Automation and click a Continue button before proceeding.';

// Who to register when a Register for Event action runs on a form-triggered path.
// ENROLLEE: the enrolled person / submitter. SPECIFIC_FIELD: a chosen name field
// (top-level + its Add Another repeats). ALL_NAME_FIELDS: every name field on
// the form (back-compat default for existing configs without registrantSource).
const registrantSourceOptions = [
    { value: 'ENROLLEE', label: 'Enrollee / Submitter' },
    { value: 'SPECIFIC_FIELD', label: 'Specific Field ID' },
    { value: 'ALL_NAME_FIELDS', label: 'All Name Fields' },
];

const registrantSourceHelpText = 'Choose who to register. "Enrollee / Submitter" registers the person in this run. "Specific Field ID" registers the person(s) from one name field on the form (paste the fieldId — use the same fieldId across connected forms). "All Name Fields" registers everyone named on the form.';

const delayUnitOptions = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
];

const delayTypeOptions = [
    { value: 'DURATION', label: 'Duration' },
    { value: 'STATIC_DATE', label: 'Static Date' },
];

const assignmentStrategyOptions = [
    { value: 'ROUND_ROBIN', label: 'Round Robin' },
    { value: 'ORDERED', label: 'Ordered' },
    { value: 'ASSIGN_ENROLLEE', label: 'Assign Enrollee' },
];

const assignmentStrategyHelpText = {
    ROUND_ROBIN: 'Assigns the next person based on who was last assigned. Wraps around to the first person after the last is reached.',
    ORDERED: 'Always starts with person 1 and moves to the next only if they decline or time out. Stops when the pool is exhausted.',
    ASSIGN_ENROLLEE: 'Assigns the task directly to the person in this run. No assignment pool is needed.',
};

// ─── Rule type options ────────────────────────────────────────────────────────

const RULE_TYPE_OPTIONS = [
    { value: 'ENROLLEE_FIELD', label: 'Enrollee Field Value' },
    { value: 'TIME_OF_DAY', label: 'Time of Day' },
    { value: 'SMALL_GROUP_STATUS', label: 'Small Group Member Status' },
    { value: 'TRAINING_STATUS', label: 'Training Module Status' },
    { value: 'REACH_USER_STATUS', label: 'Reach User Status' },
];

const ENROLLEE_REGULAR_FIELDS = [
    { value: 'person.age', label: 'Age', operators: ['equals', 'not_equals', 'greater_than', 'less_than'] },
    { value: 'person.gender', label: 'Gender', operators: ['equals', 'not_equals'] },
    { value: 'person.createdAt', label: 'Date Created', operators: ['before', 'after'] },
    { value: 'person.city', label: 'City', operators: ['equals', 'not_equals', 'contains', 'not_contains'] },
    { value: 'person.state', label: 'State', operators: ['equals', 'not_equals'] },
    { value: 'person.zip', label: 'Zip', operators: ['equals', 'not_equals'] },
    { value: 'person.isArchived', label: 'Is Archived', operators: ['equals'] },
    { value: 'person.familyRole', label: 'Family Role', operators: ['equals', 'not_equals'] },
    { value: 'person.email', label: 'Email', operators: ['has', 'does_not_have'] },
    { value: 'person.phone', label: 'Phone', operators: ['has', 'does_not_have'] },
    { value: 'person.role', label: 'Role', operators: ['has', 'does_not_have'] },
];

const FIELD_OPERATOR_LABELS = {
    equals: 'Equals',
    not_equals: 'Does Not Equal',
    greater_than: 'Greater Than',
    less_than: 'Less Than',
    contains: 'Contains',
    not_contains: 'Does Not Contain',
    before: 'Before',
    after: 'After',
    has: 'Has',
    does_not_have: 'Does Not Have',
    is_empty: 'Is Empty',
    is_not_empty: 'Is Not Empty',
};

const TIME_OF_DAY_OPTIONS = [
    { value: 'morning', label: 'Morning (5am–12pm)' },
    { value: 'afternoon', label: 'Afternoon (12pm–5pm)' },
    { value: 'evening', label: 'Evening (5pm–5am)' },
];

const GROUP_STATUS_OPTIONS = [
    { value: 'IS_IN_GROUP', label: 'Is in a group' },
    { value: 'IS_NOT_IN_GROUP', label: 'Is not in a group' },
    { value: 'HAS_COMPLETED', label: 'Has completed group' },
    { value: 'HAS_NOT_COMPLETED', label: 'Has not completed group' },
];

const TRAINING_STATUS_OPTIONS = [
    { value: 'IS_ENROLLED', label: 'Is enrolled' },
    { value: 'IS_NOT_ENROLLED', label: 'Is not enrolled' },
    { value: 'HAS_COMPLETED', label: 'Has completed' },
    { value: 'HAS_NOT_COMPLETED', label: 'Has not completed' },
];

const REACH_USER_OPTIONS = [
    { value: 'IS_REACH_USER', label: 'Is a Reach User' },
    { value: 'IS_NOT_REACH_USER', label: 'Is not a Reach User' },
];

// Operators per FORM_FIELD form-field type. 'file' fields are excluded from the
// union entirely; unknown types fall back to the text operators.
const FORM_FIELD_TEXT_OPERATORS = ['equals', 'not_equals', 'contains', 'not_contains', 'is_empty', 'is_not_empty'];
const FORM_FIELD_TYPE_OPERATORS = {
    text: FORM_FIELD_TEXT_OPERATORS,
    longtext: FORM_FIELD_TEXT_OPERATORS,
    email: FORM_FIELD_TEXT_OPERATORS,
    phone: FORM_FIELD_TEXT_OPERATORS,
    name: FORM_FIELD_TEXT_OPERATORS,
    address: FORM_FIELD_TEXT_OPERATORS,
    number: ['equals', 'not_equals', 'greater_than', 'less_than', 'is_empty', 'is_not_empty'],
    date: ['equals', 'before', 'after', 'is_empty', 'is_not_empty'],
    datetime: ['equals', 'before', 'after', 'is_empty', 'is_not_empty'],
    time: ['equals', 'before', 'after', 'is_empty', 'is_not_empty'],
    dropdown: ['equals', 'not_equals', 'is_empty', 'is_not_empty'],
    checkbox: ['equals', 'not_equals'],
    checkboxGroup: ['contains', 'not_contains', 'is_empty', 'is_not_empty'],
};

// ─── Step type options for 4-way toggle ───────────────────────────────────────

const STEP_TYPES = [
    { key: 'ACTION', label: 'Action', icon: 'flash-outline', color: 'primary' },
    { key: 'CONDITION', label: 'Condition', icon: 'git-branch-outline', color: 'warning' },
    { key: 'DELAY', label: 'Delay', icon: 'time-outline', color: 'info' },
    { key: 'TASK', label: 'Task', icon: 'checkbox-outline', color: 'success' },
];

const TYPE_HELP = {
    ACTION: 'Perform automated actions that modify data, send emails, add people to groups, and more.',
    CONDITION: 'Create different branches in your automation based on rules like age, role, or training status.',
    DELAY: 'Wait before continuing to the next step.',
    TASK: 'Create a task for someone to complete manually before continuing.',
};

const TOP_TAB_ROUTES = [
    { key: 'info', title: 'Info' },
    { key: 'options', title: 'Options' },
];

let branchCounter = 0;
const generateBranchId = () => `branch_${Date.now()}_${++branchCounter}`;

const EMPTY_DRAFT = {};

export default function CreateStepModal() {
    // Nav params are URL-driven but are lost when navigating to a sub-screen
    // (e.g. creating a Task from the Task picker via SheetSelector) and
    // returning via router.dismissTo — for a "new" step the dismissTo passes
    // no params, wiping pathID and friends. Persist them in the edit screen
    // store so they survive the round-trip. CHURCH-APP-17W.
    const { id: urlID, pathID: urlPathID, nextPosition: urlNextPosition, sourceStepID: urlSourceStepID, branchID: urlBranchID, branchLabel: urlBranchLabel, canvasPosition: urlCanvasPosition, insertOnEdgeID: urlInsertOnEdgeID, duplicateFromID: urlDuplicateFromID } = useLocalSearchParams();
    const storeKey = urlID || (urlDuplicateFromID ? "duplicate" : "new");

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData);
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData);
    const details = useEditScreenStore(s => s.CreateStepModal?.[storeKey]) || EMPTY_DRAFT;

    const storedNavParams = useEditScreenStore(s => s.CreateStepNavParams?.[storeKey]) || EMPTY_DRAFT;

    // Persist nav params whenever they're present in the URL (overwrites stale
    // values from a previous session with the same storeKey).
    useEffect(() => {
        if (urlPathID) {
            setEditScreenData("CreateStepNavParams", storeKey, {
                pathID: urlPathID,
                nextPosition: urlNextPosition,
                sourceStepID: urlSourceStepID,
                branchID: urlBranchID,
                branchLabel: urlBranchLabel,
                canvasPosition: urlCanvasPosition,
                insertOnEdgeID: urlInsertOnEdgeID,
                duplicateFromID: urlDuplicateFromID,
            });
        }
    }, [urlPathID, storeKey]);

    // Fall back to stored params when URL params are missing (after returning
    // from a sub-screen via dismissTo).
    const id = urlID || storedNavParams.id;
    const pathID = urlPathID || storedNavParams.pathID;
    const nextPosition = urlNextPosition ?? storedNavParams.nextPosition;
    const sourceStepID = urlSourceStepID || storedNavParams.sourceStepID;
    const branchID = urlBranchID || storedNavParams.branchID;
    const branchLabel = urlBranchLabel || storedNavParams.branchLabel;
    const canvasPosition = urlCanvasPosition || storedNavParams.canvasPosition;
    const insertOnEdgeID = urlInsertOnEdgeID || storedNavParams.insertOnEdgeID;
    const duplicateFromID = urlDuplicateFromID || storedNavParams.duplicateFromID;

    const router = useRouter();
    const queryClient = useQueryClient();
    const layout = useWindowDimensions();
    const isDropdownOpen = useDropdownStore((state) => state.open);

    const { data: path } = Query.paths.byId({ id: pathID });
    const existingSteps = path?.Steps || [];
    const { data: currentUser } = Query.people.loggedInUser({ options: { staleTime: Infinity } });

    const stepToEdit = id ? existingSteps.find(s => s.id === id) : (duplicateFromID ? existingSteps.find(s => s.id === duplicateFromID) : null);

    const [topTabIndex, setTopTabIndex] = useState(0);
    const [conditionTabIndex, setConditionTabIndex] = useState(0);

    // ─── Check Into Event calendar state ───────────────────────────────────
    const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
    const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
    const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
    const [eventPickerOpen, setEventPickerOpen] = useState(false);

    // ─── Trigger type + formID for variable resolution ──────────────────────
    // The path's trigger step config holds the triggerType. For FORM_ENTRY
    // triggers, the formID can come from two places:
    //   1. Path-side: triggerStep.config.triggerConfig.formID (set when a path
    //      explicitly targets a form via the path-side trigger config).
    //   2. Form-side: forms whose actions.triggerPath points at this path
    //      (the primary mechanism in the app UI — managed per-form).
    // We prefer the path-side formID if present (explicit). Otherwise we fetch
    // the triggering forms; if exactly one form triggers this path we use its
    // ID to enumerate formEntry.payload.* variables. When multiple forms
    // trigger the path, payload vars are omitted (a field from form A won't
    // exist in form B's payload) — only static formEntry vars are shown.
    // This is a v1 limitation; a per-form picker could be added later.
    const triggerStep = existingSteps.find(s => s.stepType === 'TRIGGER');
    const triggerType = triggerStep?.config?.triggerType || null;
    const isV2 = path?.schemaVersion === 2;
    const pathSideFormID = triggerStep?.config?.triggerConfig?.formID || null;
    const { data: triggeringForms } = Query.forms.triggeringPath({
        pathID,
        options: { enabled: !!pathID && triggerType === 'FORM_ENTRY' && !pathSideFormID },
    });
    const triggerFormID = pathSideFormID
        || ((triggeringForms?.length === 1) ? triggeringForms[0].id : null);
    const multipleTriggeringForms = !pathSideFormID && (triggeringForms?.length ?? 0) > 1;

    // Full form (with schema) when the path-side trigger config pins a form.
    const { data: pathSideForm } = Query.forms.byId({
        id: pathSideFormID,
        options: { enabled: !!pathSideFormID && triggerType === 'FORM_ENTRY' },
    });

    // Fetch the full variable spec ONCE (unfiltered) for this trigger type.
    // Per-param filtering (§7) is applied client-side by filterVariablesForParam.
    // This keeps a single cache entry keyed by [orgID, triggerType, formID]
    // (per §5.1) and avoids calling useQuery conditionally per action type.
    const { data: allVariablesData } = Query.paths.variables({
        triggerType,
        formID: triggerFormID,
        options: { staleTime: 1000 * 60 * 5, enabled: !!triggerType && !isV2 },
    });

    // v2: source-aware registry from automation_get_variables, plus
    // guaranteed-upstream step outputs (dominance mirrors the server check).
    const { data: v2Registry } = Query.automations.variables({
        pathID,
        options: { enabled: isV2 && !!pathID, staleTime: 1000 * 60 * 5 },
    });
    const v2StepEdges = path?.Edges || [];
    const v2UpstreamStepIDs = useMemo(() => isV2 ? computeGuaranteedUpstreamStepIDs({
        steps: existingSteps,
        edges: v2StepEdges,
        targetID: id || null,
        anchorStepID: sourceStepID || null,
    }) : [], [isV2, existingSteps, v2StepEdges, id, sourceStepID]);
    const v2Variables = useMemo(() => isV2 ? buildV2VariableOptions({
        registry: v2Registry,
        steps: existingSteps,
        upstreamStepIDs: v2UpstreamStepIDs,
    }) : [], [isV2, v2Registry, existingSteps, v2UpstreamStepIDs]);

    const allVariables = isV2 ? v2Variables : (allVariablesData || []);

    // §7 filtering: semantic takes precedence over type; no filter → all.
    // Person vars are included by the server and pass the same filters.
    const filterVariablesForParam = (paramDef) => {
        if (!paramDef) return allVariables;
        if (paramDef.semantic) return allVariables.filter(v => v.semantic === paramDef.semantic);
        if (paramDef.type) return allVariables.filter(v => v.type === paramDef.type);
        return allVariables;
    };

    // String-typed variables for text insertion (type-filtered to string)
    const stringVariables = filterVariablesForParam({ type: 'string' });

    // Load options for condition rules
    const { data: customFields } = Query.customFields.all({ module: 'People', options: { staleTime: Infinity } });
    const { data: smallGroupTypes } = Query.smallGroupTypes.all({ options: { staleTime: Infinity } });
    const { data: roles } = Query.roles.all({ options: { staleTime: Infinity } });
    const trainingModulesQuery = Query.trainingModules.all({ options: { staleTime: Infinity } });
    const trainingModules = trainingModulesQuery?.data?.pages?.flatMap(p => p.data) || [];
    const { data: emailTemplates } = Query.emailTemplates.all({ options: { staleTime: Infinity } });
    const { data: smallGroups } = Query.smallGroups.forDropdown({ options: { staleTime: Infinity } });
    const { data: boards } = Query.boards.all({ options: { staleTime: Infinity } });
    const { data: paths } = Query.paths.all({ options: { staleTime: Infinity } });
    const pathsTaskQuery = Query.tasks.byDueType({ dueType: AUTOMATION_DUE_TYPES, options: { staleTime: Infinity } });
    const { data: followUpPaths } = Query.followUp.paths({
        organizationID: currentUser?.organizationID,
        options: { enabled: isV2 && !!currentUser?.organizationID, staleTime: 1000 * 60 * 5 },
    });
    const { data: eventInstancesForRegistration } = Query.eventInstances.forRegistration({ options: { staleTime: 1000 * 60 * 5 } });

    // Monthly event instances for the Check Into Event calendar picker
    const { data: monthEventInstances, isFetching: calendarEventsLoading } = useQuery({
        queryKey: ['eventInstances', calendarMonth, calendarYear],
        queryFn: () => getMonthlyEventInstances(calendarMonth, calendarYear),
        staleTime: 1000 * 60 * 5,
    });

    // ─── TASK step: per-path assignment pool ────────────────────────────────
    // Assignment members are stored on TaskAssignmentMembers scoped to
    // (taskID, pathID) so each path using the same task can have its own pool.
    // The editor reads the existing pool for the selected task on this path;
    // user edits are tracked in details.assignmentMembers (undefined = untouched,
    // falls back to the query data; [] = explicitly cleared).
    const taskIDForMembers = details.stepType === 'TASK'
        ? (details.config?.taskID || details.config?.task?.id || null)
        : null;
    const { data: existingAssignmentMembers } = useQuery({
        queryKey: ['pathStepAssignmentMembers', pathID, taskIDForMembers],
        queryFn: async () => {
            const { data, error } = await supabase.from('TaskAssignmentMembers')
                .select('*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('pathID', pathID)
                .eq('taskID', taskIDForMembers)
                .order('position', { ascending: true });
            if (error) return [];
            return (data || []).map(m => ({ ...m, ...m.Person }));
        },
        enabled: !!pathID && !!taskIDForMembers,
        staleTime: 1000 * 60 * 5,
    });
    const assignmentMembersValue = details.assignmentMembers !== undefined
        ? details.assignmentMembers
        : (existingAssignmentMembers || []);

    // ─── FORM_FIELD condition: connected forms + field union ─────────────────
    // Connected forms come from the path-side trigger config formID (explicit)
    // or, when unset, every form whose actions.triggerPath points at this path.
    const connectedForms = useMemo(() => {
        if (pathSideFormID) return pathSideForm ? [pathSideForm] : [];
        return triggeringForms || [];
    }, [pathSideFormID, pathSideForm, triggeringForms]);

    // Union of fields across all connected form schemas, keyed by fieldId.
    // Skips hidden fields, file fields, and fields inside allowAddMore sections
    // (repeat entries can't be reliably condition-matched). First occurrence
    // wins for label/type/options; onAllForms tracks cross-form coverage.
    const formFieldUnion = useMemo(() => {
        const formCount = connectedForms.length;
        const fieldMap = new Map();
        for (const form of connectedForms) {
            const seenInForm = new Set();
            for (const section of form?.schema?.sections || []) {
                if (section.allowAddMore) continue;
                for (const field of section.fields || []) {
                    if (field.hidden || field.type === 'file') continue;
                    const fieldId = field.fieldId || field.id;
                    if (!fieldId || seenInForm.has(fieldId)) continue;
                    seenInForm.add(fieldId);
                    const existing = fieldMap.get(fieldId);
                    if (existing) {
                        existing.formIDs.add(form.id);
                    } else {
                        fieldMap.set(fieldId, {
                            fieldId,
                            label: field.label || fieldId,
                            type: field.type || 'text',
                            options: field.config?.options || [],
                            formIDs: new Set([form.id]),
                        });
                    }
                }
            }
        }
        const fields = [...fieldMap.values()].map(f => ({
            fieldId: f.fieldId,
            label: f.label,
            type: f.type,
            options: f.options,
            onAllForms: f.formIDs.size >= formCount,
        }));
        return { fields, formCount };
    }, [connectedForms]);

    const formFieldOptions = useMemo(() =>
        formFieldUnion.fields.map(f => ({
            value: f.fieldId,
            label: f.label,
            icon: { web: 'flash' },
            subtitle: f.onAllForms ? undefined : 'Not on all connected forms',
            testID: `condition-form-field-option-${f.fieldId}`,
        })),
    [formFieldUnion]);

    // Build enrollee field options (regular + custom)
    const enrolleeFieldOptions = useMemo(() => {
        const regular = ENROLLEE_REGULAR_FIELDS.map(f => ({ value: f.value, label: f.label }));
        const custom = (customFields || []).map(f => ({ value: `customField:${f.id}`, label: f.name }));
        return [...regular, ...custom];
    }, [customFields]);

    const smallGroupTypeOptions = useMemo(() =>
        (smallGroupTypes || []).map(sgt => ({ value: sgt.id, label: sgt.name })),
    [smallGroupTypes]);

    const trainingModuleOptions = useMemo(() =>
        trainingModules.map(m => ({ value: m.id, label: m.title || m.name })),
    [trainingModules]);

    const roleOptions = useMemo(() =>
        (roles || []).map(r => ({ value: r.id, label: r.name })),
    [roles]);

    // Roles assignable via the "Add to a Role" action: exclude hidden and
    // dynamic (QuickFilter-based) roles. Hidden roles are admin-only special
    // roles; dynamic roles compute membership from a filter so manually adding
    // someone doesn't make sense.
    const assignableRoleOptions = useMemo(() =>
        (roles || [])
            .filter(r => !r.isHidden && !r.QuickFilter)
            .map(r => ({ value: r.id, label: r.name })),
    [roles]);

    const emailTemplateOptions = useMemo(() =>
        (emailTemplates || []).filter(t => !t.isArchived).map(t => ({ value: t.id, label: t.name })),
    [emailTemplates]);

    const smallGroupOptions = useMemo(() =>
        (smallGroups || []).map(sg => ({ value: sg.id, label: sg.name })),
    [smallGroups]);

    const boardOptions = useMemo(() =>
        (boards || []).map(b => ({ value: b.id, label: b.name })),
    [boards]);

    const pathOptions = useMemo(() =>
        (paths || []).filter(p => p.id !== pathID).map(p => ({ value: p.id, label: p.name })),
    [paths, pathID]);

    const eventInstanceOptions = useMemo(() =>
        (eventInstancesForRegistration || [])
            .filter(i => i.Event)
            .map(i => ({
                value: i.id,
                label: `${i.Event.name} — ${new Date(i.startTime).toLocaleDateString()}`,
            })),
    [eventInstancesForRegistration]);

    useEffect(() => {
        if (stepToEdit && !Object.keys(details).length) {
            if (duplicateFromID) {
                const stepData = { ...stepToEdit };
                delete stepData.id;
                let parsedCanvasPosition;
                try {
                    parsedCanvasPosition = canvasPosition ? JSON.parse(canvasPosition) : undefined;
                } catch {
                    parsedCanvasPosition = undefined;
                }
                const config = { ...(stepToEdit.config || {}) };
                if (parsedCanvasPosition) {
                    config.canvasPosition = parsedCanvasPosition;
                } else {
                    delete config.canvasPosition;
                }
                setEditScreenData("CreateStepModal", storeKey, {
                    ...stepData,
                    config,
                    position: parseInt(nextPosition) || 1,
                });
            } else {
                setEditScreenData("CreateStepModal", storeKey, stepToEdit);
            }
        }
    }, [stepToEdit]);

    useEffect(() => {
        if (!id && !duplicateFromID && !details.stepType) {
            let parsedCanvasPosition;
            try {
                parsedCanvasPosition = canvasPosition ? JSON.parse(canvasPosition) : undefined;
            } catch {
                parsedCanvasPosition = undefined;
            }

            setEditScreenData("CreateStepModal", storeKey, {
                stepType: 'ACTION',
                actionType: 'SEND_EMAIL',
                advanceOn: 'AUTOMATIC',
                isEnabled: true,
                position: parseInt(nextPosition) || 1,
                config: parsedCanvasPosition ? { canvasPosition: parsedCanvasPosition } : {},
            });
        }
    }, []);

    const setDetails = (newDetails) => {
        setEditScreenData("CreateStepModal", storeKey, newDetails);
    };

    const saveMutation = useMutation({
        mutationFn: savePathStep,
        onSuccess: async (savedStep) => {
            if (!id && savedStep?.id) {
                if (insertOnEdgeID) {
                    await insertStepOnEdge({ edgeID: insertOnEdgeID, newStepID: savedStep.id });
                } else if (sourceStepID) {
                    const isDefaultBranch = branchID === 'default' || branchLabel === 'No Match';
                    await savePathStepEdge({
                        pathID,
                        sourceStepID,
                        targetStepID: savedStep.id,
                        branchID: isDefaultBranch ? 'default' : (branchID || null),
                        branchLabel: branchLabel || null,
                    });
                }
            }
            removeEditScreenData("CreateStepModal", storeKey);
            removeEditScreenData("CreateStepNavParams", storeKey);
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
            // Delay router.back() so a Radix portal dropdown can unmount first (CHURCH-APP-1AV)
            if (isDropdownOpen) {
                setTimeout(() => router.back(), 300);
            } else {
                router.back();
            }
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => deletePathStep({ stepID: id }),
        onSuccess: () => {
            removeEditScreenData("CreateStepModal", storeKey);
            removeEditScreenData("CreateStepNavParams", storeKey);
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
            if (isDropdownOpen) {
                setTimeout(() => router.back(), 300);
            } else {
                router.back();
            }
        },
    });

    const handleTaskSourceChange = (source) => {
        const currentConfig = details.config || {};
        if (source === 'PREDEFINED') {
            const predefinedType = currentConfig.predefinedType || 'PERSONAL_SMS';
            const typeDef = PREDEFINED_TASK_TYPES[predefinedType];
            setDetails({
                ...details,
                name: details.name?.trim() ? details.name : (typeDef?.label || details.name),
                config: {
                    ...currentConfig,
                    taskSource: 'PREDEFINED',
                    predefinedType,
                    task: undefined,
                    taskID: undefined,
                },
            });
        } else {
            const restConfig = { ...currentConfig };
            delete restConfig.taskSource;
            delete restConfig.predefinedType;
            delete restConfig.message;
            delete restConfig.subject;
            delete restConfig.body;
            delete restConfig.templateID;
            delete restConfig.callNotes;
            setDetails({
                ...details,
                config: {
                    ...restConfig,
                    taskSource: 'CUSTOM',
                    task: undefined,
                    taskID: undefined,
                },
            });
        }
    };

    const handlePredefinedTypeChange = (type) => {
        const currentConfig = details.config || {};
        const typeDef = PREDEFINED_TASK_TYPES[type];
        setDetails({
            ...details,
            name: details.name?.trim() ? details.name : (typeDef?.label || details.name),
            config: {
                ...currentConfig,
                predefinedType: type,
            },
        });
    };

    const handleSave = async () => {
        if (!details.stepType) {
            Toast.show({ type: 'error', text2: 'Please select a step type.' });
            return;
        }

        if (details.stepType === 'ACTION' && !details.actionType) {
            Toast.show({ type: 'error', text2: 'Please select an action type.' });
            return;
        }

        if (details.stepType === 'ACTION' && isV2 && (V2_ACTION_TYPE_SET.has(details.actionType) || V2_LEGACY_ACTION_SET.has(details.actionType))) {
            const v2Errors = validateV2ActionDraft(details.actionType, details.config || {}, { compatibilityMode: v2Registry?.compatibilityMode || path?.compatibilityMode });
            if (v2Errors.length) {
                Toast.show({ type: 'error', text2: v2Errors[0] });
                return;
            }
        }

        if (details.stepType === 'TASK' && details.config?.taskSource === 'PREDEFINED' && !details.config?.predefinedType) {
            Toast.show({ type: 'error', text2: 'Please select a task type.' });
            return;
        }

        let config = buildConfig();

        if (details.stepType === 'TASK' && config.taskSource === 'PREDEFINED') {
            const existingTaskID = config.taskID || details.config?.taskID;
            if (!id || !existingTaskID || duplicateFromID) {
                const predefinedType = config.predefinedType || 'PERSONAL_SMS';
                const typeDef = PREDEFINED_TASK_TYPES[predefinedType];
                const { data: taskRow, error: taskError } = await supabase
                    .from('Tasks')
                    .insert({
                        organizationID: path?.organizationID,
                        title: typeDef?.label || 'Predefined Task',
                        dueType: 'PATHS',
                    })
                    .select('id, title')
                    .maybeSingle();
                if (taskError || !taskRow) {
                    Toast.show({ type: 'error', text2: 'Failed to create the backing task. Please try again.' });
                    return;
                }
                config.taskID = taskRow.id;
                config.task = { id: taskRow.id, title: taskRow.title };
            }
        }

        saveMutation.mutate({
            id: id || undefined,
            pathID,
            position: details.position || parseInt(nextPosition) || 1,
            name: details.name?.trim() || null,
            description: details.description,
            stepType: details.stepType,
            actionType: details.stepType === 'ACTION' ? details.actionType : null,
            config,
            isEnabled: details.isEnabled ?? true,
            advanceOn: (isV2 && details.stepType === 'ACTION' && (details.actionType === 'REQUEST_APPROVAL' || details.actionType === 'WAIT_FOR_TASK'))
                ? 'AUTOMATIC'
                : (details.advanceOn || 'AUTOMATIC'),
            assignmentMembers: details.stepType === 'TASK' ? details.assignmentMembers : undefined,
        });
    };

    const buildConfig = () => {
        const config = { ...(details.config || {}) };

        if (details.stepType === 'DELAY') {
            const delayType = details.config?.delayType || 'DURATION';
            config.delayType = delayType;
            if (delayType === 'STATIC_DATE') {
                config.staticDate = details.config?.staticDate
                    ? new Date(details.config.staticDate).toISOString()
                    : null;
                delete config.duration;
                delete config.unit;
            } else {
                config.duration = parseInt(details.config?.duration) || 1;
                config.unit = details.config?.unit || 'days';
                delete config.staticDate;
            }
        }
        if (details.stepType === 'CONDITION') {
            config.branches = details.config?.branches || [];
        }
        if (details.stepType === 'TASK') {
            config.taskID = details.config?.taskID || details.config?.task?.id || null;
            config.assignmentStrategy = details.config?.assignmentStrategy || 'ROUND_ROBIN';
            config.completionTimeoutDuration = parseInt(details.config?.completionTimeoutDuration) || 1;
            config.completionTimeoutUnit = details.config?.completionTimeoutUnit || 'days';
        }
        // v2 legacy actions: explicit-target contract (no enrollee default).
        if (details.stepType === 'ACTION' && isV2 && V2_LEGACY_ACTION_SET.has(details.actionType)) {
            const built = buildV2LegacyActionConfig(details.actionType, details.config || {});
            Object.keys(config).forEach(k => delete config[k]);
            if (built) Object.assign(config, built);
        }
        // v2 action types: shape the raw draft into the worker contract.
        if (details.stepType === 'ACTION' && V2_ACTION_TYPE_SET.has(details.actionType)) {
            const raw = details.config || {};
            const builder = {
                RESOLVE_PERSON: buildResolvePersonConfig,
                UPSERT_PERSON: buildUpsertPersonConfig,
                CREATE_TASK: buildCreateTaskConfig,
                WAIT_FOR_TASK: buildWaitForTaskConfig,
                START_FOLLOW_UP_PATH: buildStartFollowUpPathConfig,
                INVOKE_AUTOMATION: buildInvokeAutomationConfig,
                REQUEST_APPROVAL: buildRequestApprovalConfig,
            }[details.actionType];
            const built = builder ? builder(raw) : {};
            Object.keys(config).forEach(k => { if (!(k in built)) delete config[k]; });
            Object.assign(config, built);
        }
        if (details.stepType === 'ACTION' && details.actionType === 'POST_TO_BOARD') {
            if (details.config?.postAs === 'me' && currentUser?.id) {
                config.postAsPersonID = currentUser.id;
            } else {
                delete config.postAsPersonID;
            }
            delete config.postAs;
        }

        return config;
    };

    const handleDelete = () => {
        showAlert({
            title: 'Delete Step',
            subtitle: `Are you sure you want to delete "${details.name || 'this step'}"? This cannot be undone.`,
            isDestructive: true,
            confirmText: 'Delete',
            onConfirm: () => deleteMutation.mutate(),
        });
    };

    const updateConfig = (key, value) => {
        setDetails({ ...details, config: { ...(details.config || {}), [key]: value } });
    };

    const handleTypeChange = (newType) => {
        const update = { ...details, stepType: newType };
        if (newType === 'ACTION' && !update.actionType) update.actionType = 'SEND_EMAIL';
        if (newType === 'CONDITION' && !update.config?.branches) {
            update.config = { ...(update.config || {}), branches: [{ id: generateBranchId(), label: '', conditions: [] }] };
            setConditionTabIndex(0);
        }
        setDetails(update);
    };

    // ─── Branch builder helpers ────────────────────────────────────────────────
    // Uses functional update to avoid stale state when batch-updating conditions

    const branches = details.config?.branches || [];

    // FORM_FIELD rule type is only offered for form-triggered paths. If an
    // existing condition already uses it (e.g. trigger changed since), keep the
    // option present so the Rule Type select isn't broken.
    const ruleTypeOptions = useMemo(() => {
        const hasFormFieldCondition = branches.some(b =>
            (b.conditions || []).some(c => c.ruleType === 'FORM_FIELD'));
        const hasStepOutputCondition = branches.some(b =>
            (b.conditions || []).some(c => c.ruleType === 'STEP_OUTPUT'));
        let options = RULE_TYPE_OPTIONS;
        if (triggerType === 'FORM_ENTRY' || hasFormFieldCondition) {
            options = [...options, { value: 'FORM_FIELD', label: 'Form Field' }];
        }
        // STEP_OUTPUT (§14.5 wait-outcome/upstream branching) — v2 only, and
        // kept visible when an existing rule already uses it.
        if (isV2 || hasStepOutputCondition) {
            options = [...options, { value: 'STEP_OUTPUT', label: 'Step Output' }];
        }
        return options;
    }, [triggerType, branches, isV2]);

    // Action Type dropdown: grouped section/subtitle options. v1 paths see
    // the v1 list only; v2 adds the v2 action types. TRIGGER_PATH is a v1
    // action — v2 hides it in favor of INVOKE_AUTOMATION, EXCEPT when the
    // step being edited already uses it so existing configs stay editable
    // (same keep-stale-value precedent as ruleTypeOptions below).
    const actionTypeSelectOptions = useMemo(() => {
        let values = V1_ACTION_TYPES;
        if (isV2) {
            values = [...V1_ACTION_TYPES, ...V2_ACTION_TYPES]
                .filter(v => v !== 'TRIGGER_PATH');
            if (details.actionType === 'TRIGGER_PATH') values.push('TRIGGER_PATH');
        }
        return buildActionTypeOptions(values);
    }, [isV2, details.actionType]);

    // Upstream step+field options for STEP_OUTPUT keys — same guaranteed-
    // upstream dominance as the variable pickers (v2UpstreamStepIDs).
    const stepOutputRuleOptions = useMemo(
        () => buildStepOutputRuleOptions({ steps: existingSteps, upstreamStepIDs: v2UpstreamStepIDs }),
        [existingSteps, v2UpstreamStepIDs]);
    const stepOutputOperatorOptions = useMemo(
        () => STEP_OUTPUT_OPERATORS.map(op => ({ value: op, label: FIELD_OPERATOR_LABELS[op] || op })),
        []);

    const updateBranches = (updater) => {
        const current = details.config?.branches || [];
        const next = typeof updater === 'function' ? updater(current) : updater;
        updateConfig('branches', next);
    };

    const addBranch = () => {
        updateBranches(prev => [...prev, { id: generateBranchId(), label: '', conditions: [] }]);
        setConditionTabIndex(branches.length);
    };

    const updateBranch = (index, key, value) => {
        updateBranches(prev => prev.map((b, i) => i === index ? { ...b, [key]: value } : b));
    };

    const removeBranch = (index) => {
        updateBranches(prev => prev.filter((_, i) => i !== index));
        setConditionTabIndex(Math.max(0, conditionTabIndex - 1));
    };

    const duplicateBranch = (index) => {
        const branchToClone = branches[index];
        if (!branchToClone) return;
        const clone = {
            id: generateBranchId(),
            label: branchToClone.label ? `${branchToClone.label} (Copy)` : '',
            conditions: (branchToClone.conditions || []).map(c => ({ ...c })),
        };
        updateBranches(prev => {
            const next = [...prev];
            next.splice(index + 1, 0, clone);
            return next;
        });
        setConditionTabIndex(index + 1);
    };

    // Batch update — accepts an object of key:value pairs to merge
    const updateConditionInBranch = (branchIndex, condIndex, updates) => {
        updateBranches(prev => prev.map((b, bi) => {
            if (bi !== branchIndex) return b;
            const conds = [...(b.conditions || [])];
            conds[condIndex] = { ...conds[condIndex], ...updates };
            return { ...b, conditions: conds };
        }));
    };

    const addConditionToBranch = (branchIndex) => {
        updateBranches(prev => prev.map((b, bi) => {
            if (bi !== branchIndex) return b;
            return { ...b, conditions: [...(b.conditions || []), { ruleType: 'ENROLLEE_FIELD', key: null, operator: null, value: null }] };
        }));
    };

    const removeConditionFromBranch = (branchIndex, condIndex) => {
        updateBranches(prev => prev.map((b, bi) => {
            if (bi !== branchIndex) return b;
            return { ...b, conditions: (b.conditions || []).filter((_, i) => i !== condIndex) };
        }));
    };

    // Get operators for a given field key
    const getOperatorsForKey = (key) => {
        if (!key) return [];
        const regular = ENROLLEE_REGULAR_FIELDS.find(f => f.value === key);
        if (regular) return regular.operators;
        if (key.startsWith('customField:')) return ['equals', 'not_equals', 'contains', 'is_empty', 'is_not_empty'];
        return [];
    };

    const getOperatorOptions = (key) => {
        const ops = getOperatorsForKey(key);
        return ops.map(op => ({ value: op, label: FIELD_OPERATOR_LABELS[op] || op }));
    };

    const fieldNeedsValue = (key, operator) => {
        if (!operator || operator === 'has' || operator === 'does_not_have' || operator === 'is_empty' || operator === 'is_not_empty') return false;
        return true;
    };

    // ─── FORM_FIELD rule helpers ──────────────────────────────────────────────
    // Operators come from FORM_FIELD_TYPE_OPERATORS keyed by form-field type —
    // deliberately separate from ENROLLEE_REGULAR_FIELDS.

    const getFormFieldMeta = (fieldId) =>
        formFieldUnion.fields.find(f => f.fieldId === fieldId) || null;

    const getFormFieldOperators = (fieldId) => {
        const meta = getFormFieldMeta(fieldId);
        if (!meta) return [];
        return FORM_FIELD_TYPE_OPERATORS[meta.type] || FORM_FIELD_TYPE_OPERATORS.text;
    };

    const getFormFieldOperatorOptions = (fieldId) =>
        getFormFieldOperators(fieldId).map(op => ({ value: op, label: FIELD_OPERATOR_LABELS[op] || op }));

    // ─── UPDATE_PERSON field rows ─────────────────────────────────────────────
    // config.fields is an object: { [personCol]: value }
    // value can be: literal string, "{{...}}" interpolation string, { var: key } binding,
    // or "__CURRENT_DATE__" / "__CURRENT_DATETIME__" sentinel for date fields.
    // We render each field as a row: field select + type-aware value input
    // (with variable insertion button + binding support).

    const updatePersonFields = details.config?.fields || {};

    // Combined list of regular People columns + custom fields (prefixed customField:)
    const allUpdatePersonFields = useMemo(() => {
        const custom = (customFields || [])
            .filter(f => !f.isDeleted)
            .map(f => ({
                value: `customField:${f.id}`,
                label: f.name,
                type: f.type,
                options: f.options,
                isCustom: true,
                customFieldID: f.id,
            }));
        return [...UPDATE_PERSON_FIELDS, ...custom];
    }, [customFields]);

    // Get field metadata for a given column key
    const getFieldMeta = (colName) => {
        return allUpdatePersonFields.find(f => f.value === colName)
            || { value: colName, label: colName, type: 'Text' };
    };

    const setUpdatePersonField = (colName, value) => {
        const fields = { ...(details.config?.fields || {}) };
        if (value === null || value === undefined) {
            delete fields[colName];
        } else {
            fields[colName] = value;
        }
        updateConfig('fields', fields);
    };

    const addUpdatePersonRow = () => {
        // Add a new empty field row (use a placeholder key that user will change)
        const fields = { ...(details.config?.fields || {}) };
        const usedCols = new Set(Object.keys(fields));
        const firstAvailable = allUpdatePersonFields.find(f => !usedCols.has(f.value));
        if (firstAvailable) {
            fields[firstAvailable.value] = '';
        }
        updateConfig('fields', fields);
    };

    const removeUpdatePersonRow = (colName) => {
        const fields = { ...(details.config?.fields || {}) };
        delete fields[colName];
        updateConfig('fields', fields);
    };

    const changeUpdatePersonFieldColumn = (oldColName, newColName) => {
        const fields = { ...(details.config?.fields || {}) };
        const value = fields[oldColName];
        delete fields[oldColName];
        // Reset value when switching field type to avoid stale values
        const oldMeta = getFieldMeta(oldColName);
        const newMeta = allUpdatePersonFields.find(f => f.value === newColName);
        const shouldReset = oldMeta?.type !== newMeta?.type;
        fields[newColName] = shouldReset ? '' : (value ?? '');
        updateConfig('fields', fields);
    };

    // Check if a value is a binding object
    const isBindingValue = (v) => v && typeof v === 'object' && !Array.isArray(v) && 'var' in v;

    // Get available field options for a row (exclude already-used columns except current)
    const getAvailableFieldOptions = (currentCol) => {
        const usedCols = new Set(Object.keys(updatePersonFields).filter(c => c !== currentCol));
        return allUpdatePersonFields.filter(f => !usedCols.has(f.value));
    };

    // Variable options for binding a person field value (string-typed variables)
    const fieldVariables = stringVariables;

    const renderUpdatePersonPanel = () => {
        const fieldEntries = Object.entries(updatePersonFields);

        return (
            <View style={styles.updatePersonPanel}>
                {fieldEntries.length === 0 && (
                    <Text style={styles.updatePersonEmpty}>No fields selected. Click &ldquo;Add Field&rdquo; to begin.</Text>
                )}
                {fieldEntries.map(([colName, value]) => {
                    const fieldMeta = getFieldMeta(colName);
                    const fieldType = fieldMeta.type || 'Text';
                    const isDateField = fieldType === 'Date' || fieldType === 'Datetime';
                    const dateSentinel = fieldType === 'Datetime' ? '__CURRENT_DATETIME__' : '__CURRENT_DATE__';
                    const useCurrentDate = value === dateSentinel;
                    const dropdownOptions = (fieldMeta.options || []).map(opt =>
                        typeof opt === 'object' ? opt : { value: opt, label: String(opt) }
                    );

                    // Normalize legacy binding objects {var: key} → "{{key}}" text tokens
                    const displayValue = isBindingValue(value) ? `{{${value.var}}}` : (value?.toString() || '');

                    return (
                        <View key={colName} style={styles.updatePersonRow}>
                            <View style={styles.updatePersonRowHeader}>
                                <Text style={styles.updatePersonRowLabel}>Field</Text>
                                <TouchableOpacity
                                    hitSlop={10}
                                    onPress={() => removeUpdatePersonRow(colName)}
                                    testID={`update-person-remove-${colName}`}
                                >
                                    <Unicon name="close-circle" size={18} colorVariant="error" />
                                </TouchableOpacity>
                            </View>
                            <SelectInput
                                title="Person Field"
                                placeholder="Select a field..."
                                value={colName}
                                options={getAvailableFieldOptions(colName)}
                                onUpdate={(newCol) => changeUpdatePersonFieldColumn(colName, newCol)}
                                hideClearButton
                            />

                            {/* ─── Type-aware value input ─── */}
                            {isDateField ? (
                                <View>
                                    <ToggleRow
                                        label="Use current date"
                                        description={fieldType === 'Datetime' ? "Set to the date & time when the step runs." : "Set to the date when the step runs."}
                                        value={useCurrentDate}
                                        onValueChange={(enabled) => {
                                            setUpdatePersonField(colName, enabled ? dateSentinel : '');
                                        }}
                                    />
                                    {!useCurrentDate && (
                                        <DatePickerInput
                                            title="Value"
                                            mode={fieldType === 'Datetime' ? 'datetime' : 'date'}
                                            value={value || null}
                                            onUpdate={(date) => setUpdatePersonField(colName, date instanceof Date ? date.toISOString() : date)}
                                        />
                                    )}
                                </View>
                            ) : fieldType === 'Dropdown' ? (
                                <SelectInput
                                    title="Value"
                                    placeholder="Select a value..."
                                    value={value}
                                    options={dropdownOptions}
                                    onUpdate={(val) => setUpdatePersonField(colName, val)}
                                />
                            ) : fieldType === 'Checkbox' ? (
                                <View style={styles.updatePersonCheckboxRow}>
                                    <Text style={styles.updatePersonCheckboxLabel}>Value</Text>
                                    <Toggle
                                        onValueChange={(val) => setUpdatePersonField(colName, val)}
                                        value={!!value}
                                    />
                                </View>
                            ) : fieldType === 'Multiselect' ? (
                                <ToggleInput
                                    title="Value"
                                    items={dropdownOptions}
                                    selectedValues={Array.isArray(value) ? value : []}
                                    onItemChanged={(selectedItem, isChecked) => {
                                        const items = Array.isArray(value) ? [...value] : [];
                                        const idx = items.indexOf(selectedItem);
                                        if (idx === -1 && isChecked) items.push(selectedItem);
                                        else if (idx !== -1 && !isChecked) items.splice(idx, 1);
                                        setUpdatePersonField(colName, items);
                                    }}
                                    itemsPerRow={2}
                                />
                            ) : (
                                /* Text, Long Text, Number, or fallback */
                                <View>
                                    <Input
                                        title="Value"
                                        placeholder="Enter value or insert a variable..."
                                        multiline={fieldType === 'Long Text'}
                                        keyboardType={fieldType === 'Number' ? 'numeric' : 'default'}
                                        value={displayValue}
                                        onUpdate={(val) => setUpdatePersonField(colName, val)}
                                    />
                                    <VariableTextInsertButton
                                        variables={fieldVariables}
                                        value={displayValue}
                                        onUpdate={(val) => setUpdatePersonField(colName, val)}
                                        testID={`update-person-variable-button-${colName}`}
                                    />
                                </View>
                            )}
                        </View>
                    );
                })}
                <Pressable
                    style={({ hovered, pressed }) => [styles.addFieldBtn, (hovered || pressed) && styles.addFieldBtnHover]}
                    onPress={addUpdatePersonRow}
                    testID="update-person-add-field"
                >
                    <Unicon name="add-circle-outline" size={18} colorVariant="primary" />
                    <Text style={styles.addFieldText}>Add Field</Text>
                </Pressable>
            </View>
        );
    };

    // ─── 4-way type toggle ─────────────────────────────────────────────────────

    const renderTypeToggle = () => (
        <View style={styles.typeToggleContainer}>
            {(isV2 && stepToEdit?.stepType !== 'TASK'
                ? STEP_TYPES.filter(t => t.key !== 'TASK')
                : STEP_TYPES).map((type) => {
                const isActive = details.stepType === type.key;
                return (
                    <TouchableOpacity
                        key={type.key}
                        style={styles.typeToggleBtn(isActive, type.color)}
                        onPress={() => handleTypeChange(type.key)}
                        activeOpacity={0.7}
                    >
                        <Unicon
                            name={type.icon}
                            size={20}
                            colorVariant={isActive ? `on${type.color.charAt(0).toUpperCase()}${type.color.slice(1)}` : type.color}
                            style={Platform.OS === 'web' ? { marginRight: 6 } : { marginBottom: 4 }}
                        />
                        <Text style={styles.typeToggleText(isActive, type.color)}>
                            {type.label}
                        </Text>
                    </TouchableOpacity>
                );
            })}
        </View>
    );

    // ─── FORM_FIELD rule row ──────────────────────────────────────────────────
    // Field select (union across connected forms) + type-aware operator + value.
    // Config: { ruleType: 'FORM_FIELD', key: <fieldId>, operator, value }.

    const renderFormFieldRule = (branchIndex, condIndex, condition) => {
        const fieldMeta = getFormFieldMeta(condition.key);
        const fieldType = fieldMeta?.type || 'text';
        const isStaleKey = !!condition.key && !fieldMeta;
        const showCoverageWarning = !!fieldMeta && !fieldMeta.onAllForms && formFieldUnion.formCount > 1;
        const hasConnectedForms = formFieldUnion.formCount > 0;
        // Options may be strings or { value, label } objects depending on form vintage
        const choiceOptions = (fieldMeta?.options || []).map(opt =>
            typeof opt === 'object' ? opt : { value: opt, label: String(opt) }
        );
        const setValue = (val) => updateConditionInBranch(branchIndex, condIndex, { value: val });
        const rowTestID = `condition-form-field-${branchIndex}-${condIndex}`;

        const renderValueInput = () => {
            if (!fieldMeta || !fieldNeedsValue(condition.key, condition.operator)) return null;
            if (fieldType === 'date' || fieldType === 'datetime' || fieldType === 'time') {
                return (
                    <DatePickerInput
                        title="Value"
                        mode={fieldType === 'time' ? 'time' : fieldType === 'datetime' ? 'datetime' : 'date'}
                        value={condition.value || null}
                        onUpdate={(date) => setValue(date instanceof Date ? date.toISOString() : date)}
                        testID={`${rowTestID}-value`}
                    />
                );
            }
            if (fieldType === 'dropdown' || fieldType === 'checkboxGroup') {
                return (
                    <SelectInput
                        title="Value"
                        placeholder="Select a value..."
                        value={condition.value}
                        options={choiceOptions}
                        onUpdate={setValue}
                        testID={`${rowTestID}-value`}
                    />
                );
            }
            if (fieldType === 'checkbox') {
                return (
                    <View style={styles.updatePersonCheckboxRow}>
                        <Text style={styles.updatePersonCheckboxLabel}>Value</Text>
                        <Toggle
                            value={condition.value === true}
                            onValueChange={setValue}
                            testID={`${rowTestID}-value`}
                        />
                    </View>
                );
            }
            return (
                <Input
                    title="Value"
                    placeholder="Enter value..."
                    multiline={fieldType === 'longtext'}
                    keyboardType={fieldType === 'number' ? 'numeric' : 'default'}
                    value={condition.value != null ? String(condition.value) : ''}
                    onUpdate={setValue}
                    testID={`${rowTestID}-value`}
                />
            );
        };

        return (
            <View style={styles.conditionFieldsRow}>
                <View style={styles.conditionField}>
                    <SelectInput
                        title="Field"
                        placeholder="Select a form field..."
                        value={condition.key}
                        options={formFieldOptions}
                        onUpdate={(val) => {
                            const ops = getFormFieldOperators(val);
                            updateConditionInBranch(branchIndex, condIndex, {
                                key: val,
                                operator: ops[0] || null,
                                value: null,
                            });
                        }}
                        testID={rowTestID}
                    />
                    {!hasConnectedForms && (
                        <Text style={styles.formFieldHelperText} testID="condition-form-field-no-forms">
                            Connect a form to this Automation to select fields.
                        </Text>
                    )}
                    {isStaleKey && (
                        <Text style={styles.staleWarning} testID="condition-form-field-stale-warning">
                            Field no longer available: {condition.key}
                        </Text>
                    )}
                    {showCoverageWarning && (
                        <Text style={styles.staleWarning} testID="condition-form-field-coverage-warning">
                            This field isn&apos;t on all connected forms — the condition only applies when the submitted form includes it.
                        </Text>
                    )}
                </View>
                {fieldMeta && (
                    <View style={styles.conditionField}>
                        <SelectInput
                            title="Operator"
                            value={condition.operator}
                            options={getFormFieldOperatorOptions(condition.key)}
                            onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { operator: val })}
                            testID={`${rowTestID}-operator`}
                        />
                    </View>
                )}
                {fieldMeta && fieldNeedsValue(condition.key, condition.operator) && (
                    <View style={styles.conditionField}>
                        {renderValueInput()}
                    </View>
                )}
            </View>
        );
    };

    // ─── Condition rule renderer ───────────────────────────────────────────────

    const renderConditionRule = (branchIndex, condIndex, condition) => {
        const ruleType = condition.ruleType || 'ENROLLEE_FIELD';

        return (
            <View key={condIndex} style={styles.conditionRuleRow}>
                <View style={styles.conditionRuleHeader}>
                    <Text style={styles.conditionRuleLabel}>
                        {condIndex === 0 ? 'If' : 'And if'}
                    </Text>
                    <TouchableOpacity hitSlop={10} onPress={() => removeConditionFromBranch(branchIndex, condIndex)}>
                        <Unicon name="close-circle" size={18} colorVariant="error" />
                    </TouchableOpacity>
                </View>

                <SelectInput
                    title="Rule Type"
                    value={ruleType}
                    options={ruleTypeOptions}
                    onUpdate={(val) => {
                        // Single batched update — fixes the stale-state bug
                        updateConditionInBranch(branchIndex, condIndex, {
                            ruleType: val,
                            key: null,
                            operator: null,
                            value: null,
                        });
                    }}
                />

                {ruleType === 'ENROLLEE_FIELD' && (
                    <View style={styles.conditionFieldsRow}>
                        <View style={styles.conditionField}>
                            <SelectInput
                                title="Field"
                                value={condition.key}
                                options={enrolleeFieldOptions}
                                onUpdate={(val) => {
                                    const ops = getOperatorsForKey(val);
                                    updateConditionInBranch(branchIndex, condIndex, {
                                        key: val,
                                        operator: ops[0] || null,
                                        value: null,
                                    });
                                }}
                            />
                        </View>
                        {condition.key && (
                            <View style={styles.conditionField}>
                                <SelectInput
                                    title="Operator"
                                    value={condition.operator}
                                    options={getOperatorOptions(condition.key)}
                                    onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { operator: val })}
                                />
                            </View>
                        )}
                        {fieldNeedsValue(condition.key, condition.operator) && condition.key === 'person.role' && (
                            <View style={styles.conditionField}>
                                <SelectInput
                                    title="Role"
                                    value={condition.value}
                                    options={roleOptions}
                                    onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { value: val })}
                                />
                            </View>
                        )}
                        {fieldNeedsValue(condition.key, condition.operator) && condition.key !== 'person.role' && (
                            <View style={styles.conditionField}>
                                <Input
                                    title="Value"
                                    placeholder="Enter value..."
                                    value={condition.value || ''}
                                    onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { value: val })}
                                />
                            </View>
                        )}
                    </View>
                )}

                {ruleType === 'FORM_FIELD' && renderFormFieldRule(branchIndex, condIndex, condition)}

                {ruleType === 'STEP_OUTPUT' && (() => {
                    // {ruleType:'STEP_OUTPUT', key:'<pathStepID>.<field>'} —
                    // the worker's wait-outcome/upstream branch rule (v2 only).
                    const rowTestID = `condition-step-output-${branchIndex}-${condIndex}`;
                    const known = stepOutputRuleOptions.some(o => o.value === condition.key);
                    const needsValue = fieldNeedsValue(condition.key, condition.operator);
                    return (
                        <View style={styles.conditionFieldsRow}>
                            <View style={styles.conditionField}>
                                <SelectInput
                                    title="Step Output"
                                    placeholder="Select an upstream output..."
                                    value={condition.key}
                                    options={stepOutputRuleOptions}
                                    onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, {
                                        key: val,
                                        operator: condition.operator || 'equals',
                                        value: null,
                                    })}
                                    testID={rowTestID}
                                />
                                {stepOutputRuleOptions.length === 0 && (
                                    <Text style={styles.formFieldHelperText} testID={`${rowTestID}-no-upstream`}>
                                        No upstream step outputs are available here. Add a step that produces outputs (like Create Task or Resolve Person) before this condition.
                                    </Text>
                                )}
                                {!!condition.key && !known && (
                                    <Text style={styles.staleWarning} testID={`${rowTestID}-stale-warning`}>
                                        Output no longer guaranteed upstream: {condition.key}
                                    </Text>
                                )}
                            </View>
                            {!!condition.key && (
                                <View style={styles.conditionField}>
                                    <SelectInput
                                        title="Operator"
                                        value={condition.operator}
                                        options={stepOutputOperatorOptions}
                                        onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { operator: val })}
                                        testID={`${rowTestID}-operator`}
                                    />
                                </View>
                            )}
                            {!!condition.key && needsValue && (
                                <View style={styles.conditionField}>
                                    <Input
                                        title="Value"
                                        placeholder="Enter value..."
                                        value={condition.value != null ? String(condition.value) : ''}
                                        onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { value: val })}
                                        testID={`${rowTestID}-value`}
                                    />
                                </View>
                            )}
                        </View>
                    );
                })()}

                {ruleType === 'TIME_OF_DAY' && (
                    <View style={styles.conditionFieldsRow}>
                        <View style={styles.conditionField}>
                            <SelectInput
                                title="Time of Day"
                                value={condition.value}
                                options={TIME_OF_DAY_OPTIONS}
                                onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { value: val })}
                            />
                        </View>
                    </View>
                )}

                {ruleType === 'SMALL_GROUP_STATUS' && (
                    <View style={styles.conditionFieldsRow}>
                        <View style={styles.conditionField}>
                            <SelectInput
                                title="Group Type"
                                value={condition.key}
                                options={smallGroupTypeOptions}
                                onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { key: val })}
                            />
                        </View>
                        <View style={styles.conditionField}>
                            <SelectInput
                                title="Status"
                                value={condition.value}
                                options={GROUP_STATUS_OPTIONS}
                                onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { value: val })}
                            />
                        </View>
                    </View>
                )}

                {ruleType === 'TRAINING_STATUS' && (
                    <View style={styles.conditionFieldsRow}>
                        <View style={styles.conditionField}>
                            <SelectInput
                                title="Training Module"
                                value={condition.key}
                                options={trainingModuleOptions}
                                onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { key: val })}
                            />
                        </View>
                        <View style={styles.conditionField}>
                            <SelectInput
                                title="Status"
                                value={condition.value}
                                options={TRAINING_STATUS_OPTIONS}
                                onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { value: val })}
                            />
                        </View>
                    </View>
                )}

                {ruleType === 'REACH_USER_STATUS' && (
                    <View style={styles.conditionFieldsRow}>
                        <View style={styles.conditionField}>
                            <SelectInput
                                title="User Status"
                                value={condition.value}
                                options={REACH_USER_OPTIONS}
                                onUpdate={(val) => updateConditionInBranch(branchIndex, condIndex, { value: val })}
                            />
                        </View>
                    </View>
                )}
            </View>
        );
    };

    // ─── Condition panel with branch tabs ──────────────────────────────────────

    const renderConditionPanel = () => {
        const currentBranch = branches[conditionTabIndex];
        const branchTabRoutes = branches.map((b, i) => ({
            key: b.id || `branch_${i}`,
            title: b.label?.trim() || `Branch ${i + 1}`,
        }));

        return (
            <View style={styles.conditionPanel}>
                {/* Branch tabs header with New Branch button */}
                <View style={styles.branchTabsHeader}>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.branchTabsScroll}>
                        {branchTabRoutes.map((route, i) => {
                            const isActive = i === conditionTabIndex;
                            return (
                                <TouchableOpacity
                                    key={route.key}
                                    style={styles.branchTab(isActive)}
                                    onPress={() => setConditionTabIndex(i)}
                                    activeOpacity={0.7}
                                >
                                    <Text style={styles.branchTabText(isActive)}>{route.title}</Text>
                                    <View style={styles.branchTabActions}>
                                        <TouchableOpacity
                                            hitSlop={8}
                                            onPress={() => duplicateBranch(i)}
                                            style={styles.branchTabIconBtn}
                                        >
                                            <Ionicons name="copy-outline" size={13} color={theme => isActive ? theme.colors.onWarning : theme.colors.warning} />
                                        </TouchableOpacity>
                                        {branches.length > 1 && (
                                            <TouchableOpacity
                                                hitSlop={8}
                                                onPress={() => removeBranch(i)}
                                                style={styles.branchTabIconBtn}
                                            >
                                                <Ionicons name="close" size={14} color={theme => isActive ? theme.colors.onWarning : theme.colors.warning} />
                                            </TouchableOpacity>
                                        )}
                                    </View>
                                </TouchableOpacity>
                            );
                        })}
                    </ScrollView>
                    <Pressable
                        style={({ hovered, pressed }) => [styles.newBranchBtn, (hovered || pressed) && styles.newBranchBtnHover]}
                        onPress={addBranch}
                    >
                        <Unicon name="add" size={18} colorVariant="warning" />
                        <Text style={styles.newBranchText}>New Branch</Text>
                    </Pressable>
                </View>

                {/* Current branch content */}
                {currentBranch && (
                    <KeyboardAwareScrollView style={styles.branchContent} contentContainerStyle={styles.branchContentInner}>
                        <Input
                            title="Branch Label"
                            placeholder="e.g. Under 18, Active Member..."
                            value={currentBranch.label || ''}
                            onUpdate={(val) => updateBranch(conditionTabIndex, 'label', val)}
                        />
                        <Text style={styles.branchHint}>This label appears on the flow editor. Each branch leads to its own sub-flow.</Text>

                        {(currentBranch.conditions || []).map((condition, condIndex) =>
                            renderConditionRule(conditionTabIndex, condIndex, condition)
                        )}

                        <Pressable
                            style={({ hovered, pressed }) => [styles.addConditionBtn, (hovered || pressed) && styles.addConditionBtnHover]}
                            onPress={() => addConditionToBranch(conditionTabIndex)}
                        >
                            <Unicon name="add-circle-outline" size={18} colorVariant="primary" />
                            <Text style={styles.addConditionText}>Add Condition</Text>
                        </Pressable>

                        <Text style={styles.defaultBranchNote}>
                            A &ldquo;No Match&rdquo; branch is automatically shown in the flow editor for any enrollee who doesn&apos;t match any branch above.
                        </Text>
                    </KeyboardAwareScrollView>
                )}
            </View>
        );
    };

    // ─── Check Into Event: calendar + event instance picker ─────────────────────

    const calendarEventsByDate = useMemo(() => {
        const map = {};
        for (const inst of (monthEventInstances || [])) {
            const dateStr = new Date(inst.startTime).toDateString();
            if (!map[dateStr]) map[dateStr] = [];
            map[dateStr].push({
                id: inst.id,
                name: inst.nameOverride || inst.Event?.name || 'Untitled Event',
                startTime: inst.startTime,
                allDay: inst.allDay === true,
                color: inst.Event?.Calendar?.color,
            });
        }
        return map;
    }, [monthEventInstances]);

    const calendarMarkedDates = useMemo(() => {
        const map = {};
        for (const inst of (monthEventInstances || [])) {
            const dateStr = new Date(inst.startTime).toDateString();
            if (!map[dateStr]) map[dateStr] = [];
            const color = inst.Event?.Calendar?.color;
            if (color && !map[dateStr].includes(color)) map[dateStr].push(color);
        }
        return map;
    }, [monthEventInstances]);

    const selectedDateInstances = useMemo(() => {
        if (!selectedCalendarDate) return [];
        const dateStr = selectedCalendarDate.toDateString();
        return calendarEventsByDate[dateStr] || [];
    }, [selectedCalendarDate, calendarEventsByDate]);

    const selectedEventInstance = useMemo(() => {
        const id = details.config?.eventInstanceID;
        if (!id) return null;
        return (monthEventInstances || []).find(i => i.id === id) || null;
    }, [details.config?.eventInstanceID, monthEventInstances]);

    const renderCheckIntoEventCalendar = () => {
        const selectedName = selectedEventInstance
            ? (selectedEventInstance.nameOverride || selectedEventInstance.Event?.name || 'Untitled Event')
            : null;
        const selectedDateStr = selectedEventInstance
            ? new Date(selectedEventInstance.startTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
            : null;
        const selectedTimeStr = selectedEventInstance
            ? new Date(selectedEventInstance.startTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
            : null;

        return (
            <View style={styles.checkInCalendarContainer}>
                {/* Input-style trigger */}
                <Pressable
                    style={styles.checkInEventTrigger}
                    onPress={() => setEventPickerOpen(true)}
                    testID="checkin-event-trigger"
                >
                    <Unicon name="calendar-outline" size={18} colorVariant={selectedEventInstance ? 'events' : 'textTertiary'} />
                    <View style={styles.checkInEventTriggerText}>
                        {selectedEventInstance ? (
                            <>
                                <Text style={styles.checkInEventTriggerName} numberOfLines={1}>{selectedName}</Text>
                                <Text style={styles.checkInEventTriggerSub} numberOfLines={1}>{selectedDateStr} · {selectedTimeStr}</Text>
                            </>
                        ) : (
                            <Text style={styles.checkInEventTriggerPlaceholder}>Choose Event</Text>
                        )}
                    </View>
                    {selectedEventInstance ? (
                        <TouchableOpacity
                            hitSlop={8}
                            onPress={() => updateConfig('eventInstanceID', null)}
                            style={styles.checkInEventTriggerClear}
                            testID="checkin-event-clear"
                        >
                            <Unicon name="close-circle" size={18} colorVariant="textTertiary" />
                        </TouchableOpacity>
                    ) : (
                        <Unicon name="chevron-down" size={18} colorVariant="textTertiary" />
                    )}
                </Pressable>

                {/* Modal with calendar + event list */}
                <SheetModal
                    isOpen={eventPickerOpen}
                    title="Choose Event"
                    subtitle="Select an event instance to check the enrollee into"
                    onCancel={() => {
                        setEventPickerOpen(false);
                        setSelectedCalendarDate(null);
                    }}
                >
                    <View style={styles.checkInModalBody}>
                        <Calendar
                            eventsByDate={calendarEventsByDate}
                            markedDates={calendarMarkedDates}
                            eventsAreLoading={calendarEventsLoading}
                            onDayPress={(date) => setSelectedCalendarDate(date)}
                            onMonthChange={(m) => { setCalendarMonth(m); setSelectedCalendarDate(null); }}
                            onYearChange={(y) => { setCalendarYear(y); setSelectedCalendarDate(null); }}
                            canCreateEvents={false}
                            onEventPress={(event) => {
                                updateConfig('eventInstanceID', event.id);
                                setEventPickerOpen(false);
                                setSelectedCalendarDate(null);
                            }}
                            initialSelectedDate={selectedCalendarDate || undefined}
                        />

                        {selectedCalendarDate && (
                            <View style={styles.checkInEventList}>
                                <Text style={styles.checkInEventListLabel}>
                                    Events on {selectedCalendarDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                </Text>
                                {selectedDateInstances.length === 0 ? (
                                    <Text style={styles.checkInEventEmpty}>No events on this day.</Text>
                                ) : (
                                    selectedDateInstances.map(inst => {
                                        const isSelectedInstance = details.config?.eventInstanceID === inst.id;
                                        const timeStr = new Date(inst.startTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
                                        return (
                                            <TouchableOpacity
                                                key={inst.id}
                                                style={[styles.checkInEventItem, isSelectedInstance && styles.checkInEventItemSelected]}
                                                onPress={() => {
                                                    updateConfig('eventInstanceID', inst.id);
                                                    setEventPickerOpen(false);
                                                    setSelectedCalendarDate(null);
                                                }}
                                                testID={`checkin-event-instance-${inst.id}`}
                                            >
                                                <View style={styles.checkInEventItemInfo}>
                                                    <Text style={[styles.checkInEventItemName, isSelectedInstance && styles.checkInEventItemNameSelected]} numberOfLines={1}>
                                                        {inst.name}
                                                    </Text>
                                                    <Text style={[styles.checkInEventItemTime, isSelectedInstance && styles.checkInEventItemTimeSelected]}>
                                                        {timeStr}
                                                    </Text>
                                                </View>
                                                {isSelectedInstance && (
                                                    <Ionicons name="checkmark-circle" size={20} color={theme => theme.colors.primary} />
                                                )}
                                            </TouchableOpacity>
                                        );
                                    })
                                )}
                            </View>
                        )}
                    </View>
                </SheetModal>
            </View>
        );
    };

    // ─── Type-specific options panels ──────────────────────────────────────────

    const renderOptionsPanel = () => {
        const helpText = TYPE_HELP[details.stepType];

        switch (details.stepType) {
            case 'ACTION':
                return (
                    <KeyboardAwareScrollView style={styles.panelScroll} contentContainerStyle={styles.panelInner}>
                        <Text style={styles.helpTextCentered}>{helpText}</Text>

                        <SelectInput
                            title="Action Type"
                            value={details.actionType || 'SEND_EMAIL'}
                            options={actionTypeSelectOptions}
                            onUpdate={(val) => setDetails({ ...details, actionType: val })}
                        />

                        {isV2 && ACTION_PERSON_INPUTS[details.actionType] && (
                            <View>
                                <Text style={styles.helpText}>
                                    Choose who this action applies to — specific people, a person variable from the trigger, or an earlier step.
                                </Text>
                                <PeopleTargetInput
                                    title={ACTION_PERSON_INPUTS[details.actionType].label}
                                    required
                                    multiple={ACTION_PERSON_INPUTS[details.actionType].multiple === true}
                                    variables={allVariables}
                                    value={details.config?.[ACTION_PERSON_INPUTS[details.actionType].key]}
                                    onUpdate={(val) => updateConfig(ACTION_PERSON_INPUTS[details.actionType].key, val)}
                                    testID={`v2-target-${details.actionType.toLowerCase()}`}
                                    fromScreen="paths/steps/create"
                                    fromStoreKey={storeKey}
                                />
                            </View>
                        )}

                        {isV2 && V2_ACTION_TYPE_SET.has(details.actionType) && (
                            <V2ActionConfigPanel
                                actionType={details.actionType}
                                config={details.config || {}}
                                updateConfig={updateConfig}
                                variables={allVariables}
                                lookups={{
                                    paths,
                                    followUpPaths: followUpPaths || [],
                                    pathsTaskQuery,
                                    stringVariables,
                                    storeKey,
                                    pathID,
                                }}
                            />
                        )}

                        {!(isV2 && (details.actionType === 'REQUEST_APPROVAL' || details.actionType === 'WAIT_FOR_TASK')) && (
                            <SelectInput
                                title="Advance Behavior"
                                helpText={advanceOnHelpText}
                                value={details.advanceOn || 'AUTOMATIC'}
                                options={advanceOnOptions}
                                onUpdate={(val) => setDetails({ ...details, advanceOn: val })}
                            />
                        )}

                        {multipleTriggeringForms && (
                            <Text style={styles.variableHelpText}>
                                Note: This automation is triggered by multiple forms. Form-answer variables are not shown because field IDs differ per form. Person variables and static form-entry variables are still available.
                            </Text>
                        )}

                        {details.actionType === 'SEND_EMAIL' && (
                            <>
                                <Text style={styles.variableHelpText}>
                                    Note: Trigger variables are NOT interpolated inside email templates. Use SMS or board posts for trigger variable interpolation, or use person merge tags (e.g. {'{{firstName}}'}) inside email templates.
                                </Text>
                                <VariableSelectInput
                                    title="Email Template"
                                    placeholder="Select a template..."
                                    literalOptions={emailTemplateOptions}
                                    variables={filterVariablesForParam(ACTION_PARAM_DEFS.SEND_EMAIL.templateID)}
                                    paramDef={ACTION_PARAM_DEFS.SEND_EMAIL.templateID}
                                    value={details.config?.templateID}
                                    onUpdate={(val) => updateConfig('templateID', val)}
                                />
                                {Platform.OS === 'web' && (
                                    <TouchableOpacity onPress={() => window.open('/emails/editor', '_blank')} style={styles.linkBtn}>
                                        <Text style={styles.linkText}>+ Build a new email template</Text>
                                    </TouchableOpacity>
                                )}
                            </>
                        )}

                        {details.actionType === 'SEND_SMS' && (
                            <View>
                                <View style={styles.textWithVariableRow}>
                                    <View style={styles.textInputFlex}>
                                        <Input
                                            title="Message"
                                            placeholder="Enter SMS message. Use {{firstName}} or {{person.firstName}} for variables."
                                            multiline
                                            value={details.config?.message || ''}
                                            onUpdate={(val) => updateConfig('message', val)}
                                        />
                                    </View>
                                    <VariableTextInsertButton
                                        variables={stringVariables}
                                        value={details.config?.message || ''}
                                        onUpdate={(val) => updateConfig('message', val)}
                                        testID="sms-message-variable-button"
                                    />
                                </View>
                            </View>
                        )}

                        {details.actionType === 'ENROLL_TRAINING' && (
                            <VariableSelectInput
                                title="Training Module"
                                placeholder="Select a module..."
                                literalOptions={trainingModuleOptions}
                                variables={filterVariablesForParam(ACTION_PARAM_DEFS.ENROLL_TRAINING.trainingModuleID)}
                                paramDef={ACTION_PARAM_DEFS.ENROLL_TRAINING.trainingModuleID}
                                value={details.config?.trainingModuleID}
                                onUpdate={(val) => updateConfig('trainingModuleID', val)}
                            />
                        )}

                        {details.actionType === 'ADD_TO_SMALL_GROUP' && (
                            <VariableSelectInput
                                title="Small Group"
                                placeholder="Select a group..."
                                literalOptions={smallGroupOptions}
                                variables={filterVariablesForParam(ACTION_PARAM_DEFS.ADD_TO_SMALL_GROUP.smallGroupID)}
                                paramDef={ACTION_PARAM_DEFS.ADD_TO_SMALL_GROUP.smallGroupID}
                                value={details.config?.smallGroupID}
                                onUpdate={(val) => updateConfig('smallGroupID', val)}
                            />
                        )}

                        {details.actionType === 'POST_TO_BOARD' && (
                            <>
                                <VariableSelectInput
                                    title="Board"
                                    placeholder="Select a board..."
                                    literalOptions={boardOptions}
                                    variables={filterVariablesForParam(ACTION_PARAM_DEFS.POST_TO_BOARD.boardID)}
                                    paramDef={ACTION_PARAM_DEFS.POST_TO_BOARD.boardID}
                                    value={details.config?.boardID}
                                    onUpdate={(val) => updateConfig('boardID', val)}
                                />
                                {!isV2 && (
                                    <SelectInput
                                        title="Post As"
                                        placeholder="Select who to post as..."
                                        value={details.config?.postAs || 'enrollee'}
                                        options={[
                                            { value: 'enrollee', label: 'Run Person (default)' },
                                            { value: 'me', label: 'Me' },
                                        ]}
                                        onUpdate={(val) => updateConfig('postAs', val)}
                                    />
                                )}
                                <View style={styles.textWithVariableRow}>
                                    <View style={styles.textInputFlex}>
                                        <Input
                                            title="Post Title"
                                            placeholder="Enter post title..."
                                            value={details.config?.title || ''}
                                            onUpdate={(val) => updateConfig('title', val)}
                                        />
                                    </View>
                                    <VariableTextInsertButton
                                        variables={stringVariables}
                                        value={details.config?.title || ''}
                                        onUpdate={(val) => updateConfig('title', val)}
                                        testID="board-title-variable-button"
                                    />
                                </View>
                                <View style={styles.textWithVariableRow}>
                                    <View style={styles.textInputFlex}>
                                        <Input
                                            title="Post Content"
                                            placeholder="Enter post content..."
                                            multiline
                                            value={details.config?.content || ''}
                                            onUpdate={(val) => updateConfig('content', val)}
                                        />
                                    </View>
                                    <VariableTextInsertButton
                                        variables={stringVariables}
                                        value={details.config?.content || ''}
                                        onUpdate={(val) => updateConfig('content', val)}
                                        testID="board-content-variable-button"
                                    />
                                </View>
                            </>
                        )}

                        {details.actionType === 'ADD_TO_ROLE' && (
                            <VariableSelectInput
                                title="Role"
                                placeholder="Select a role..."
                                literalOptions={assignableRoleOptions}
                                variables={filterVariablesForParam(ACTION_PARAM_DEFS.ADD_TO_ROLE.roleID)}
                                paramDef={ACTION_PARAM_DEFS.ADD_TO_ROLE.roleID}
                                value={details.config?.roleID}
                                onUpdate={(val) => updateConfig('roleID', val)}
                            />
                        )}

                        {details.actionType === 'INVITE_TO_REACH' && (
                            <Text style={styles.helpText}>Sends a Reach invitation to the enrollee. No additional configuration needed.</Text>
                        )}

                        {details.actionType === 'TRIGGER_PATH' && (
                            <VariableSelectInput
                                title="Automation to Trigger"
                                placeholder="Select an automation..."
                                literalOptions={pathOptions}
                                variables={filterVariablesForParam(ACTION_PARAM_DEFS.TRIGGER_PATH.targetPathID)}
                                paramDef={ACTION_PARAM_DEFS.TRIGGER_PATH.targetPathID}
                                value={details.config?.targetPathID}
                                onUpdate={(val) => updateConfig('targetPathID', val)}
                            />
                        )}

                        {details.actionType === 'UPDATE_PERSON' && (
                            <View>
                                <Text style={styles.helpText}>Select fields to update on the person record. Field values can be literal text, contain {'{{variable}}'} tokens, or be bound to a trigger variable.</Text>
                                {renderUpdatePersonPanel()}
                            </View>
                        )}

                        {details.actionType === 'REGISTER_FOR_EVENT' && (
                            <>
                                <VariableSelectInput
                                    title="Event Instance"
                                    placeholder="Select an event instance..."
                                    literalOptions={eventInstanceOptions}
                                    variables={filterVariablesForParam(ACTION_PARAM_DEFS.REGISTER_FOR_EVENT.eventInstanceID)}
                                    paramDef={ACTION_PARAM_DEFS.REGISTER_FOR_EVENT.eventInstanceID}
                                    value={details.config?.eventInstanceID}
                                    onUpdate={(val) => updateConfig('eventInstanceID', val)}
                                />
                                {isV2 ? (
                                    <>
                                        <PeopleTargetInput
                                            title="People to register"
                                            multiple
                                            variables={allVariables}
                                            value={details.config?.registrantPersonIDs}
                                            onUpdate={(val) => updateConfig('registrantPersonIDs', val)}
                                            testID="v2-register-people"
                                            fromScreen="paths/steps/create"
                                            fromStoreKey={storeKey}
                                        />
                                        <Text style={styles.helpText}>
                                            …or bind a structured registrant list (name, email, phone) from a variable — e.g. repeating form answers.
                                        </Text>
                                        <AutomationBindingInput
                                            title="Registrant list variable"
                                            placeholder="Bind a registrants list..."
                                            variables={filterVariablesForParam({ type: 'list' })}
                                            value={details.config?.registrants}
                                            onUpdate={(val) => updateConfig('registrants', val)}
                                            allowManualEntry={false}
                                            testID="v2-register-list"
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Text style={styles.helpText}>{registrantSourceHelpText}</Text>
                                        <SelectInput
                                            title="Who to Register"
                                            placeholder="Select who to register..."
                                            value={details.config?.registrantSource || 'ENROLLEE'}
                                            options={registrantSourceOptions}
                                            onUpdate={(val) => updateConfig('registrantSource', val)}
                                        />
                                    </>
                                )}
                                {!isV2 && details.config?.registrantSource === 'SPECIFIC_FIELD' && (
                                    <Input
                                        title="Name Field ID"
                                        placeholder="e.g. adults_and_teens"
                                        value={details.config?.registrantFieldId || ''}
                                        onUpdate={(val) => updateConfig('registrantFieldId', val)}
                                    />
                                )}
                            </>
                        )}

                        {details.actionType === 'CHECK_INTO_EVENT' && (
                            <View>
                                <Text style={styles.helpText}>Select an event instance to check the enrollee into.</Text>
                                {renderCheckIntoEventCalendar()}
                            </View>
                        )}
                    </KeyboardAwareScrollView>
                );

            case 'CONDITION':
                return (
                    <View style={styles.panelFlex}>
                        <Text style={styles.helpTextCentered}>{helpText}</Text>
                        {renderConditionPanel()}
                    </View>
                );

            case 'DELAY':
                return (
                    <KeyboardAwareScrollView style={styles.panelScroll} contentContainerStyle={styles.panelInner}>
                        <Text style={styles.helpTextCentered}>{helpText}</Text>

                        <SelectInput
                            title="Delay Type"
                            value={details.config?.delayType || 'DURATION'}
                            options={delayTypeOptions}
                            onUpdate={(val) => updateConfig('delayType', val)}
                            helpText="Duration waits a relative amount of time (e.g. 3 days). Static Date waits until a specific date and time."
                        />

                        {(details.config?.delayType || 'DURATION') === 'DURATION' ? (
                            <View style={styles.inlineRow}>
                                <View style={styles.inlineInputFlex}>
                                    <Input
                                        title="Duration"
                                        placeholder="1"
                                        keyboardType="numeric"
                                        value={String(details.config?.duration || '')}
                                        onUpdate={(val) => updateConfig('duration', val)}
                                    />
                                </View>
                                <View style={[styles.inlineSelectFixed, { marginTop: 0 }]}>
                                    <SelectInput
                                        title="Unit"
                                        value={details.config?.unit || 'days'}
                                        options={delayUnitOptions}
                                        onUpdate={(val) => updateConfig('unit', val)}
                                    />
                                </View>
                            </View>
                        ) : (
                            <>
                                <DatePickerInput
                                    title="Run On"
                                    mode="datetime"
                                    minuteInterval={5}
                                    value={details.config?.staticDate ? new Date(details.config.staticDate) : null}
                                    onUpdate={(val) => updateConfig('staticDate', val ? new Date(val).toISOString() : null)}
                                />
                                {details.config?.staticDate && new Date(details.config.staticDate) < new Date() && (
                                    <View style={styles.warningRow}>
                                        <Unicon name="warning-outline" size={16} colorVariant="warning" />
                                        <Text style={styles.warningText}>This date is in the past, so the automation won{"\u2019"}t ever run past this point.</Text>
                                    </View>
                                )}
                            </>
                        )}
                    </KeyboardAwareScrollView>
                );

            case 'TASK': {
                const currentStrategy = details.config?.assignmentStrategy || 'ROUND_ROBIN';
                const showPool = currentStrategy !== 'ASSIGN_ENROLLEE';
                const isPredefined = details.config?.taskSource === 'PREDEFINED';
                const currentPredefinedType = details.config?.predefinedType || 'PERSONAL_SMS';
                return (
                    <KeyboardAwareScrollView style={styles.panelScroll} contentContainerStyle={styles.panelInner}>
                        <Text style={styles.helpTextCentered}>{helpText}</Text>

                        <View style={styles.taskSourceToggleContainer}>
                            <TouchableOpacity
                                style={styles.taskSourceBtn(isPredefined)}
                                onPress={() => handleTaskSourceChange('PREDEFINED')}
                                activeOpacity={0.7}
                                testID="task-source-predefined"
                            >
                                <Text style={styles.taskSourceBtnText(isPredefined)}>Predefined</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.taskSourceBtn(!isPredefined)}
                                onPress={() => handleTaskSourceChange('CUSTOM')}
                                activeOpacity={0.7}
                                testID="task-source-custom"
                            >
                                <Text style={styles.taskSourceBtnText(!isPredefined)}>Custom</Text>
                            </TouchableOpacity>
                        </View>

                        {isPredefined ? (
                            <>
                                <View testID="predefined-type">
                                    <SelectInput
                                        title="Task Type"
                                        value={details.config?.predefinedType || 'PERSONAL_SMS'}
                                        options={PREDEFINED_TASK_TYPE_OPTIONS}
                                        onUpdate={handlePredefinedTypeChange}
                                    />
                                </View>
                                <Text style={styles.secondaryText}>When the assignee opens this task on mobile, Reach opens their SMS / phone / email app with the enrollee&rsquo;s contact info prefilled.</Text>

                                {currentPredefinedType === 'PERSONAL_SMS' && (
                                    <View style={styles.textWithVariableRow}>
                                        <View style={styles.textInputFlex}>
                                            <Input
                                                title="Message"
                                                placeholder="Enter SMS message. Use {{firstName}}, {{lastName}}, or {{fullName}} for variables."
                                                multiline
                                                value={details.config?.message || ''}
                                                onUpdate={(val) => updateConfig('message', val)}
                                            />
                                        </View>
                                        <VariableTextInsertButton
                                            variables={stringVariables}
                                            value={details.config?.message || ''}
                                            onUpdate={(val) => updateConfig('message', val)}
                                            testID="predefined-sms-message-variable-button"
                                        />
                                    </View>
                                )}

                                {currentPredefinedType === 'PERSONAL_EMAIL' && (
                                    <>
                                        <SelectInput
                                            title="Email Template"
                                            placeholder="Select a template..."
                                            value={details.config?.templateID}
                                            options={emailTemplateOptions}
                                            onUpdate={(val) => updateConfig('templateID', val)}
                                            helpText="Optional reference for the sender. The message below is what gets prefilled into their email app."
                                        />
                                        <View style={styles.textWithVariableRow}>
                                            <View style={styles.textInputFlex}>
                                                <Input
                                                    title="Subject"
                                                    placeholder="Enter email subject..."
                                                    value={details.config?.subject || ''}
                                                    onUpdate={(val) => updateConfig('subject', val)}
                                                />
                                            </View>
                                            <VariableTextInsertButton
                                                variables={stringVariables}
                                                value={details.config?.subject || ''}
                                                onUpdate={(val) => updateConfig('subject', val)}
                                                testID="predefined-email-subject-variable-button"
                                            />
                                        </View>
                                        <View style={styles.textWithVariableRow}>
                                            <View style={styles.textInputFlex}>
                                                <Input
                                                    title="Message"
                                                    placeholder="Enter email body. Use {{firstName}}, {{lastName}}, or {{fullName}} for variables."
                                                    multiline
                                                    value={details.config?.body || ''}
                                                    onUpdate={(val) => updateConfig('body', val)}
                                                />
                                            </View>
                                            <VariableTextInsertButton
                                                variables={stringVariables}
                                                value={details.config?.body || ''}
                                                onUpdate={(val) => updateConfig('body', val)}
                                                testID="predefined-email-body-variable-button"
                                            />
                                        </View>
                                    </>
                                )}

                                {currentPredefinedType === 'PERSONAL_PHONE_CALL' && (
                                    <View>
                                        <View style={styles.textWithVariableRow}>
                                            <View style={styles.textInputFlex}>
                                                <Input
                                                    title="Talking Points"
                                                    placeholder="What should the assignee talk about? Use {{firstName}}, {{lastName}}, or {{fullName}} for variables."
                                                    multiline
                                                    value={details.config?.callNotes || ''}
                                                    onUpdate={(val) => updateConfig('callNotes', val)}
                                                />
                                            </View>
                                            <VariableTextInsertButton
                                                variables={stringVariables}
                                                value={details.config?.callNotes || ''}
                                                onUpdate={(val) => updateConfig('callNotes', val)}
                                                testID="predefined-call-notes-variable-button"
                                            />
                                        </View>
                                        <Text style={styles.secondaryText}>Talking points are displayed on the task for the assignee to read &mdash; nothing is prefilled into the phone app.</Text>
                                    </View>
                                )}
                            </>
                        ) : (
                            <SheetSelector
                                type="tasks"
                                title="Task"
                                required
                                addScreenName="tasks/create"
                                addScreenParams={{ dueType: 'PATHS' }}
                                fromScreen="paths/steps/create"
                                fromStoreKey={storeKey}
                                queryResult={pathsTaskQuery}
                                value={details.config?.task || null}
                                onChange={(task) => {
                                    // Reset the assignment pool so it reloads for the
                                    // newly selected task (undefined = fall back to query).
                                    setDetails({
                                        ...details,
                                        config: { ...(details.config || {}), task, taskID: task?.id },
                                        assignmentMembers: undefined,
                                    });
                                }}
                            />
                        )}

                        <SelectInput
                            title="Assignment Strategy"
                            value={currentStrategy}
                            options={assignmentStrategyOptions}
                            onUpdate={(val) => updateConfig('assignmentStrategy', val)}
                            helpText={assignmentStrategyHelpText[currentStrategy]}
                        />
                        {showPool && (
                            <>
                                <SheetSelector
                                    type="people"
                                    title="Assignment Members"
                                    fromScreen="paths/steps/create"
                                    fromStoreKey={storeKey}
                                    multiple
                                    value={assignmentMembersValue}
                                    onChange={(selected_items) => {
                                        setDetails({ ...details, assignmentMembers: selected_items });
                                    }}
                                />
                                <Text style={styles.secondaryText}>These are the people who can be assigned to this task for this automation. If none are added, the task is created unassigned and can be claimed by anyone who can edit the automation.</Text>
                                <View style={styles.inlineRow}>
                                    <View style={styles.inlineInputFlex}>
                                        <Input
                                            title="Completion Timeout"
                                            placeholder="1"
                                            keyboardType="numeric"
                                            value={String(details.config?.completionTimeoutDuration || '')}
                                            onUpdate={(val) => updateConfig('completionTimeoutDuration', val)}
                                            helpText="If the task is not accepted or completed within this time, it automatically moves to the next person in the assignment pool."
                                        />
                                    </View>
                                    <View style={styles.inlineSelectFixed}>
                                        <SelectInput
                                            title="Unit"
                                            value={details.config?.completionTimeoutUnit || 'days'}
                                            options={delayUnitOptions}
                                            onUpdate={(val) => updateConfig('completionTimeoutUnit', val)}
                                        />
                                    </View>
                                </View>
                            </>
                        )}
                    </KeyboardAwareScrollView>
                );
            }

            default:
                return null;
        }
    };

    // ─── Top-level tab rendering ───────────────────────────────────────────────

    const renderTopTabBar = (props) => (
        <ReachTabBar
            {...props}
            scrollEnabled={false}
            indicatorStyle={{ backgroundColor: theme => theme.colors.primary, height: 3, borderTopLeftRadius: 3, borderTopRightRadius: 3 }}
        />
    );

    const renderTopScene = ({ route }) => {
        switch (route.key) {
            case 'info':
                return (
                    <KeyboardAwareScrollView style={styles.tabContent} contentContainerStyle={styles.tabContentInner}>
                        <Input
                            title="Step Name"
                            placeholder="e.g. Send Welcome Email"
                            value={details.name || ''}
                            onUpdate={(val) => setDetails({ ...details, name: val })}
                        />
                        <Input
                            title="Description"
                            placeholder="Describe what this step does..."
                            multiline
                            value={details.description || ''}
                            onUpdate={(val) => setDetails({ ...details, description: val })}
                        />
                        <ToggleRow
                            label="Enabled"
                            description="When disabled, this step will be skipped during enrollment."
                            value={details.isEnabled ?? true}
                            onValueChange={(val) => setDetails({ ...details, isEnabled: val })}
                        />
                    </KeyboardAwareScrollView>
                );
            case 'options':
                return (
                    <View style={styles.optionsTab}>
                        {renderTypeToggle()}
                        {renderOptionsPanel()}
                    </View>
                );
            default:
                return null;
        }
    };

    return (
        <ModalWrapper
            title={id ? "Edit Step" : (duplicateFromID ? "Duplicate Step" : "Add Step")}
            width={620}
            confirmText="Save"
            loading={saveMutation.isPending || deleteMutation.isPending}
            onConfirm={handleSave}
            onCancel={() => {
                removeEditScreenData("CreateStepModal", storeKey);
                removeEditScreenData("CreateStepNavParams", storeKey);
            }}
        >
            <View style={{ flex: 1 }}>
                <TabView
                    navigationState={{ index: topTabIndex, routes: TOP_TAB_ROUTES }}
                    renderScene={renderTopScene}
                    onIndexChange={setTopTabIndex}
                    renderTabBar={renderTopTabBar}
                    initialLayout={{ width: layout.width }}
                    style={{ flex: 1 }}
                    lazy
                />

                <View style={styles.bottomSection}>
                    {id && (
                        <View style={styles.deleteContainer}>
                            <Text style={styles.deleteButton} onPress={handleDelete}>Delete Step</Text>
                        </View>
                    )}
                </View>
            </View>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create((theme) => ({
    tabContent: {
        // flex: 1,
    },
    tabContentInner: {
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 24,
    },
    optionsTab: {
        flex: 1,
    },
    helpText: {
        fontSize: 13,
        lineHeight: 18,
        color: theme.colors.textTertiary,
        marginBottom: 12,
        fontStyle: 'italic',
        paddingHorizontal: 16,
        paddingTop: 12,
    },
    helpTextCentered: {
        fontSize: 13,
        lineHeight: 18,
        color: theme.colors.textTertiary,
        marginBottom: 12,
        fontStyle: 'italic',
        textAlign: 'center',
        paddingHorizontal: 16,
        paddingTop: 12,
    },
    secondaryText: {
        fontStyle: 'italic',
        fontSize: 12,
        paddingTop: 2,
        paddingBottom: 8,
        color: theme.colors.textSecondary,
    },
    linkBtn: {
        paddingVertical: 8,
        paddingHorizontal: 12,
    },
    linkText: {
        fontSize: 13,
        color: theme.colors.primary,
        fontFamily: theme.fonts.heading,
    },
    bottomSection: {
        paddingHorizontal: 16,
        paddingTop: 8,
    },
    inlineRow: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'flex-start',
    },
    inlineInputFlex: {
        flex: 1,
    },
    inlineSelectFixed: {
        flex: 1,
        marginTop: 3,
    },
    warningRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8,
        paddingHorizontal: 16,
        paddingTop: 4,
    },
    warningText: {
        flex: 1,
        fontSize: 13,
        lineHeight: 18,
        color: theme.colors.warning,
        fontFamily: theme.fonts.body,
    },

    // 4-way type toggle
    typeToggleContainer: {
        flexDirection: 'row',
        paddingHorizontal: 16,
        paddingTop: 12,
        gap: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    typeToggleBtn: (isActive, color) => ({
        flex: 1,
        maxWidth: 120,
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: Platform.OS === 'web' ? 10 : 12,
        paddingHorizontal: 8,
        borderRadius: 10,
        backgroundColor: isActive ? theme.colors[color] : theme.colors[color + 'Container'],
        borderWidth: 0,
        _web: {
            transition: 'transform 0.15s ease, background-color 0.15s ease',
            cursor: 'pointer',
            userSelect: 'none',
            _hover: { transform: [{ scale: 1.03 }] },
            _active: { transform: [{ scale: 0.97 }] },
        },
    }),
    typeToggleText: (isActive, color) => ({
        fontSize: Platform.OS === 'web' ? 13 : 11,
        fontFamily: theme.fonts.heading,
        color: isActive ? theme.colors[`on${color.charAt(0).toUpperCase()}${color.slice(1)}`] : theme.colors[color],
        marginTop: Platform.OS === 'web' ? 0 : 2,
    }),

    // 2-way Predefined/Custom task source toggle
    taskSourceToggleContainer: {
        flexDirection: 'row',
        paddingHorizontal: 16,
        paddingTop: 12,
        gap: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    taskSourceBtn: (isActive) => ({
        flex: 1,
        maxWidth: 160,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 10,
        backgroundColor: isActive ? theme.colors.primary : theme.colors.primaryContainer,
        borderWidth: 0,
        _web: {
            transition: 'transform 0.15s ease, background-color 0.15s ease',
            cursor: 'pointer',
            userSelect: 'none',
            _hover: { transform: [{ scale: 1.03 }] },
            _active: { transform: [{ scale: 0.97 }] },
        },
    }),
    taskSourceBtnText: (isActive) => ({
        fontSize: Platform.OS === 'web' ? 13 : 12,
        fontFamily: theme.fonts.heading,
        color: isActive ? theme.colors.onPrimary : theme.colors.primary,
    }),

    // Options panels
    panelScroll: {
        flex: 1,
    },
    panelInner: {
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 24,
    },
    panelFlex: {
        flex: 1,
    },

    // Condition panel
    conditionPanel: {
        flex: 1,
    },
    branchTabsHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingTop: 8,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        gap: 8,
    },
    branchTabsScroll: {
        flex: 1,
    },
    branchTab: (isActive) => ({
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        alignItems: 'center',
        gap: Platform.OS === 'web' ? 4 : 2,
        paddingVertical: Platform.OS === 'web' ? 6 : 8,
        paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
        borderRadius: 8,
        marginRight: 6,
        backgroundColor: isActive ? theme.colors.warning : theme.colors.warningContainer,
        borderWidth: 0,
        _web: {
            transition: 'transform 0.15s ease, background-color 0.15s ease',
            cursor: 'pointer',
            userSelect: 'none',
            _hover: { transform: [{ scale: 1.04 }] },
            _active: { transform: [{ scale: 0.96 }] },
        },
    }),
    branchTabText: (isActive) => ({
        fontSize: Platform.OS === 'web' ? 13 : 11,
        fontFamily: theme.fonts.heading,
        color: isActive ? theme.colors.onWarning : theme.colors.warning,
        marginTop: Platform.OS === 'web' ? 0 : 2,
    }),
    branchTabClose: {
        marginLeft: 2,
    },
    branchTabActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        marginLeft: 4,
    },
    branchTabIconBtn: {
        padding: 2,
    },
    newBranchBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 8,
        backgroundColor: theme.colors.warningContainer,
        borderWidth: 0,
    },
    newBranchBtnHover: {
        backgroundColor: theme.colors.warningSubtle,
    },
    newBranchText: {
        fontSize: 12,
        fontFamily: theme.fonts.heading,
        color: theme.colors.warning,
    },
    branchContent: {
        flex: 1,
    },
    branchContentInner: {
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 24,
    },
    branchHint: {
        fontSize: 11,
        color: theme.colors.textTertiary,
        marginTop: 4,
        marginBottom: 12,
        fontStyle: 'italic',
    },

    // Condition rule rows
    conditionRuleRow: {
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 8,
        padding: 10,
        marginBottom: 8,
    },
    conditionFieldsRow: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'flex-start',
    },
    conditionField: {
        flex: 1,
    },
    conditionRuleHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 6,
    },
    conditionRuleLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
    },
    addConditionBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.primary,
        borderStyle: 'dashed',
        marginTop: 4,
    },
    addConditionBtnHover: {
        backgroundColor: theme.colors.primaryContainer,
        borderStyle: 'solid',
    },
    addConditionText: {
        fontSize: 13,
        fontFamily: theme.fonts.heading,
        color: theme.colors.primary,
    },
    defaultBranchNote: {
        fontSize: 12,
        color: theme.colors.textTertiary,
        textAlign: 'center',
        fontStyle: 'italic',
        lineHeight: 16,
        marginTop: 12,
    },

    deleteContainer: {
        alignItems: 'center',
        paddingVertical: 20,
    },
    deleteButton: {
        fontSize: 14,
        fontFamily: theme.fonts.heading,
        color: theme.colors.error,
    },

    // Variable help text (SEND_EMAIL note)
    variableHelpText: {
        fontSize: 12,
        lineHeight: 16,
        color: theme.colors.textTertiary,
        fontStyle: 'italic',
        paddingHorizontal: 4,
        paddingTop: 8,
        paddingBottom: 12,
    },

    // Text input + variable button row
    textWithVariableRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8,
    },
    textInputFlex: {
        flex: 1,
    },

    // Stale binding warning
    staleWarning: {
        fontSize: 12,
        color: theme.colors.error,
        fontStyle: 'italic',
        marginTop: 4,
        marginBottom: 8,
    },

    // FORM_FIELD helper text (no connected forms)
    formFieldHelperText: {
        fontSize: 12,
        color: theme.colors.textTertiary,
        fontStyle: 'italic',
        marginTop: 4,
        marginBottom: 8,
    },

    // UPDATE_PERSON panel
    updatePersonPanel: {
        gap: 12,
    },
    updatePersonEmpty: {
        fontSize: 13,
        color: theme.colors.textTertiary,
        fontStyle: 'italic',
        textAlign: 'center',
        paddingVertical: 16,
    },
    updatePersonRow: {
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 8,
        padding: 10,
    },
    updatePersonRowHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    updatePersonRowLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
    },
    updatePersonBindingRow: {
        marginTop: 8,
    },
    updatePersonBindingLabel: {
        fontSize: 11,
        color: theme.colors.textTertiary,
        fontStyle: 'italic',
        marginBottom: 4,
    },
    updatePersonCheckboxRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 10,
        paddingHorizontal: 15,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.border,
        marginTop: 4,
    },
    updatePersonCheckboxLabel: {
        fontSize: 14,
        color: theme.colors.text,
    },
    boundValueDisplay: {
        flexDirection: 'column',
        gap: 4,
    },
    boundValueLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
    },
    boundValueChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryContainer,
    },
    boundValueText: {
        fontSize: 13,
        fontFamily: theme.fonts.heading,
        color: theme.colors.primary,
        flexShrink: 1,
    },
    addFieldBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.primary,
        borderStyle: 'dashed',
        marginTop: 4,
    },
    addFieldBtnHover: {
        backgroundColor: theme.colors.primaryContainer,
        borderStyle: 'solid',
    },
    addFieldText: {
        fontSize: 13,
        fontFamily: theme.fonts.heading,
        color: theme.colors.primary,
    },

    // Check Into Event calendar
    checkInCalendarContainer: {
        marginTop: 4,
    },
    checkInEventTrigger: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        height: 42,
        paddingHorizontal: 14,
        borderWidth: 1,
        borderColor: theme.colors.border,
        borderRadius: 10,
        backgroundColor: theme.colors.surface,
        _web: {
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            cursor: 'pointer',
            userSelect: 'none',
            _hover: { borderColor: theme.colors.outline },
        },
    },
    checkInEventTriggerText: {
        flex: 1,
        justifyContent: 'center',
    },
    checkInEventTriggerName: {
        fontSize: 14,
        color: theme.colors.text,
        fontFamily: theme.fonts.heading,
    },
    checkInEventTriggerSub: {
        fontSize: 11,
        color: theme.colors.textSecondary,
        marginTop: 1,
    },
    checkInEventTriggerPlaceholder: {
        fontSize: 15,
        color: theme.colors.textTertiary,
    },
    checkInEventTriggerClear: {
        padding: 2,
    },
    checkInModalBody: {
        padding: 16,
        paddingBottom: 32,
    },
    checkInEventList: {
        marginTop: 12,
        gap: 6,
    },
    checkInEventListLabel: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        marginBottom: 4,
    },
    checkInEventEmpty: {
        fontSize: 13,
        color: theme.colors.textTertiary,
        fontStyle: 'italic',
    },
    checkInEventItem: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 8,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    checkInEventItemSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryContainer,
    },
    checkInEventItemInfo: {
        flex: 1,
    },
    checkInEventItemName: {
        fontSize: 14,
        color: theme.colors.text,
        fontFamily: theme.fonts.heading,
    },
    checkInEventItemNameSelected: {
        color: theme.colors.primary,
    },
    checkInEventItemTime: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    checkInEventItemTimeSelected: {
        color: theme.colors.primary,
    },
}));
